// App.js - Complete WebRTC SIP Client with Enhanced CDR Management
// IMPORTANT: This file requires JsSIP - install with: npm install jssip
// Usage: Copy this file to your project's src/App.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Users, BarChart3, Clock, User, LogOut, Play, Pause, PhoneForwarded, Upload, Download, Tag, FileText, X, Plus, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import JsSIP from 'jssip';
import apiService from './apiService';
import QueueManagement from './QueueManagement';
import MissedCallNotifications from './MissedCallNotifications';
import ContactBook from './ContactBook';
import PhoneNumberInput from './components/PhoneNumberInput';
import CategoryManagement from './components/CategoryManagement';
import { recordMetric, generateReport } from './utils/simplePerformance';
import performanceMonitor from './utils/performance';

const WebRTCSIPClient = () => {
  // View and Auth State
  const [currentView, setCurrentView] = useState('queues');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [user, setUser] = useState(null);

  // SIP State
  const [sipConnected, setSipConnected] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [callNumber, setCallNumber] = useState('');
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callStatus, setCallStatus] = useState('');
  const [incomingCall, setIncomingCall] = useState(null);
  const [callerInfo, setCallerInfo] = useState(null);
  const [onHold, setOnHold] = useState(false);
  
  // Connection Quality Monitoring
  const [connectionQuality, setConnectionQuality] = useState({
    status: 'good', // 'good', 'fair', 'poor', 'disconnected'
    rtt: 0, // Round trip time in ms
    packetLoss: 0, // Percentage
    jitter: 0, // ms
    lastCheck: null,
    history: [], // Last 10 measurements for trend analysis
    score: 100 // Overall quality score (0-100)
  });
  
  // Advanced monitoring state
  const [networkTrends, setNetworkTrends] = useState({
    degrading: false, // Connection getting worse
    improving: false, // Connection getting better
    unstable: false, // High variance in RTT
    recommendation: null // Smart recommendation
  });
  
  // Microphone Status
  const [microphoneStatus, setMicrophoneStatus] = useState({
    available: false,
    checking: true,
    error: null
  });
  const [showMicWarning, setShowMicWarning] = useState(false);

  // Hold Duration Tracking
  const [holdStartTime, setHoldStartTime] = useState(null);
  const [totalHoldDuration, setTotalHoldDuration] = useState(0);
  const [holdCount, setHoldCount] = useState(0);
  const [currentHoldDuration, setCurrentHoldDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null); // Track when call started

  //Tags
  const [newTag, setNewTag] = useState({ tag_name: '', tag_color: '#3B82F6', tag_category: '' });
  //const [editingTag, setEditingTag] = useState(null);

  // Dark Theme State
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true; // Default to dark mode
  });

  // Ringtone Settings
  const [customRingtone, setCustomRingtone] = useState(() => {
    const saved = localStorage.getItem('customRingtone');
    // If user uploaded custom ringtone (data URL), use it
    if (saved && saved.startsWith('data:')) {
      return saved;
    }
    // Otherwise use default file
    return '/sounds/ringtone.mp3';
  });
  const [ringtoneVolume, setRingtoneVolume] = useState(() => {
    const saved = localStorage.getItem('ringtoneVolume');
    return saved ? parseFloat(saved) : 0.5;
  });

  // Call End Sound Settings
  const [callEndSoundEnabled, setCallEndSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('callEndSoundEnabled');
    return saved ? JSON.parse(saved) : true; // Enabled by default
  });

  // Language Settings
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'uk';
  });

  // Auto-refresh Settings
  const [autoRefreshCDR, setAutoRefreshCDR] = useState(() => {
    const saved = localStorage.getItem('autoRefreshCDR');
    return saved ? JSON.parse(saved) : false;
  });

  // Translations
  const translations = {
    uk: {
      // Navigation
      queues: 'Черги',
      callHistory: 'Історія дзвінків',
      analytics: 'Аналітика',
      myStatistics: 'Моя статистика',
      kpiReport: 'KPI звіт',
      tagsManagement: 'Управління тегами',
      userManagement: 'Управління користувачами',
      queueManagement: 'Управління чергами',
      allQueues: 'Всі черги',
      settings: 'Налаштування',
      
      // Phone Bar
      callDuration: 'Тривалість дзвінка',
      holdTime: 'Час утримання',
      totalHold: 'Загальний час утримання',
      holdCount: 'Кількість утримань',
      mute: 'Вимкнути мікрофон',
      unmute: 'Увімкнути мікрофон',
      hold: 'Утримати',
      resume: 'Відновити',
      transfer: 'Переадресувати',
      endCall: 'Завершити дзвінок',
      call: 'Подзвонити',
      
      // Call Status
      dialing: 'Набір...',
      ringing: 'Дзвонить...',
      connected: 'З\'єднано',
      incomingCall: 'Вхідний дзвінок',
      accept: 'Прийняти',
      reject: 'Відхилити',
      
      // Settings
      ringtoneSettings: 'Налаштування дзвінка',
      currentRingtone: 'Поточний дзвінок',
      custom: 'Власний',
      default: 'За замовчуванням (згенерований тон)',
      uploadRingtone: 'Завантажити власний дзвінок',
      ringtoneVolume: 'Гучність дзвінка',
      callEndSound: 'Звук завершення дзвінка',
      test: 'Тест',
      remove: 'Видалити',
      theme: 'Тема',
      darkMode: 'Темна тема',
      language: 'Мова',
      
      // Common
      logout: 'Вийти',
      login: 'Увійти',
      username: 'Ім\'я користувача',
      password: 'Пароль',
      save: 'Зберегти',
      cancel: 'Скасувати',
      close: 'Закрити',
      search: 'Пошук',
      filter: 'Фільтр',
      export: 'Експорт',
      import: 'Імпорт',
      clearHistory: 'Очистити історію',
      confirmClearHistory: 'Очистити історію дзвінків?',
      noCallHistory: 'Історія дзвінків порожня',
      copyForJira: 'Копіювати для Jira',
      copiedToClipboard: 'Скопійовано в буфер обміну!',
      failedToCopy: 'Помилка копіювання',
      refresh: 'Оновити',
      autoRefresh: 'Авто-оновлення',
      importCSV: 'Імпорт CSV',
      exportCSV: 'Експорт CSV',
      
      // Login
      appTitle: 'Comfy Call Center',
      signInLDAP: 'Увійдіть за допомогою облікових даних LDAP',
      ldapUsername: 'LDAP користувач',
      passwordPlaceholder: 'Пароль',
      
      // Queue Management
      queueManagementTitle: 'Управління чергами',
      myActiveQueues: 'Мої активні черги',
      availableQueues: 'Доступні черги',
      waitingCallers: 'Очікують абоненти',
      live: 'Наживо',
      
      // Call Details
      callDetails: 'Деталі дзвінка',
      addNote: 'Додати нотатку...',
      
      // Import/Export
      importCDRFromCSV: 'Імпорт CDR з CSV',
      uploadCSVPreview: 'Завантажте CSV файл для попереднього перегляду',
      selectFile: 'Вибрати файл',
      columnMapping: 'Відповідність колонок',
      
      // Filters
      startDate: 'Дата початку',
      endDate: 'Дата кінця',
      searchAgent: 'Пошук агента...',
      
      // Statistics
      totalCalls: 'Всього дзвінків',
      answered: 'Відповіли',
      missed: 'Пропущені',
      answerRate: 'Відсоток відповідей',
      avgDuration: 'Середня тривалість',
      callsByHour: 'Дзвінки по годинах',
      totalTalkTime: 'Загальний час розмов',
      avgCallTime: 'Середній час дзвінка',
      queue: 'Черга',
      direction: 'Напрямок',
      
      // Queue Stats
      totalQueues: 'Всього черг',
      totalWaiting: 'Всього очікують',
      totalAgents: 'Всього агентів',
      availableAgents: 'Доступні агенти',
      queueName: 'Назва черги',
      waiting: 'Очікують',
      agents: 'Агенти',
      noAgents: 'Немає агентів',
      
      // Tags
      addNewTag: 'Додати новий тег',
      tagName: 'Назва тегу',
      color: 'Колір',
      category: 'Категорія',
      existingTags: 'Існуючі теги',
      
      // User Management
      createUser: 'Створити користувача',
      editUser: 'Редагувати користувача',
      fullName: 'Повне ім\'я',
      email: 'Email',
      sipNumber: 'SIP номер',
      permissions: 'Дозволи',
      queueAccess: 'Доступ до черг',
      isAdmin: 'Адміністратор',
      canViewKPI: 'Перегляд KPI',
      canManageQueues: 'Управління чергами',
      canEditTags: 'Редагування тегів',
      canManageUsers: 'Управління користувачами',
      
      // Time format
      seconds: 'сек',
      minutes: 'хв',
      hours: 'год'
    },
    en: {
      // Navigation
      queues: 'Queues',
      callHistory: 'Call History',
      analytics: 'Analytics',
      myStatistics: 'My Statistics',
      kpiReport: 'KPI Report',
      tagsManagement: 'Tags Management',
      userManagement: 'User Management',
      queueManagement: 'Queue Management',
      allQueues: 'All Queues',
      settings: 'Settings',
      
      // Phone Bar
      callDuration: 'Call Duration',
      holdTime: 'Hold Time',
      totalHold: 'Total Hold Time',
      holdCount: 'Hold Count',
      mute: 'Mute',
      unmute: 'Unmute',
      hold: 'Hold',
      resume: 'Resume',
      transfer: 'Transfer',
      endCall: 'End Call',
      call: 'Call',
      
      // Call Status
      dialing: 'Dialing...',
      ringing: 'Ringing...',
      connected: 'Connected',
      incomingCall: 'Incoming Call',
      accept: 'Accept',
      reject: 'Reject',
      
      // Settings
      ringtoneSettings: 'Ringtone Settings',
      currentRingtone: 'Current Ringtone',
      custom: 'Custom',
      default: 'Default (Generated Tone)',
      uploadRingtone: 'Upload Custom Ringtone',
      ringtoneVolume: 'Ringtone Volume',
      callEndSound: 'Call End Sound',
      test: 'Test',
      remove: 'Remove',
      theme: 'Theme',
      darkMode: 'Dark Mode',
      language: 'Language',
      
      // Common
      logout: 'Logout',
      login: 'Login',
      username: 'Username',
      password: 'Password',
      save: 'Save',
      cancel: 'Cancel',
      close: 'Close',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      clearHistory: 'Clear History',
      confirmClearHistory: 'Clear call history?',
      noCallHistory: 'No call history yet',
      copyForJira: 'Copy for Jira',
      copiedToClipboard: 'Copied to clipboard!',
      failedToCopy: 'Failed to copy',
      refresh: 'Refresh',
      autoRefresh: 'Auto-refresh',
      importCSV: 'Import CSV',
      exportCSV: 'Export CSV',
      
      // Login
      appTitle: 'Comfy Call Center',
      signInLDAP: 'Sign in with LDAP credentials',
      ldapUsername: 'LDAP username',
      passwordPlaceholder: 'Password',
      
      // Queue Management
      queueManagementTitle: 'Queue Management',
      myActiveQueues: 'My Active Queues',
      availableQueues: 'Available Queues',
      waitingCallers: 'Waiting Callers',
      live: 'Live',
      
      // Call Details
      callDetails: 'Call Details',
      addNote: 'Add a note...',
      
      // Import/Export
      importCDRFromCSV: 'Import CDR from CSV',
      uploadCSVPreview: 'Upload CSV file to preview',
      selectFile: 'Select File',
      columnMapping: 'Column Mapping',
      
      // Filters
      startDate: 'Start Date',
      endDate: 'End Date',
      searchAgent: 'Search agent...',
      
      // Statistics
      totalCalls: 'Total Calls',
      answered: 'Answered',
      missed: 'Missed',
      answerRate: 'Answer Rate',
      avgDuration: 'Avg Duration',
      callsByHour: 'Calls by Hour',
      totalTalkTime: 'Total Talk Time',
      avgCallTime: 'Avg Call Time',
      queue: 'Queue',
      direction: 'Direction',
      
      // Queue Stats
      totalQueues: 'Total Queues',
      totalWaiting: 'Total Waiting',
      totalAgents: 'Total Agents',
      availableAgents: 'Available Agents',
      queueName: 'Queue Name',
      waiting: 'Waiting',
      agents: 'Agents',
      noAgents: 'No agents',
      
      // Tags
      addNewTag: 'Add New Tag',
      tagName: 'Tag Name',
      color: 'Color',
      category: 'Category',
      existingTags: 'Existing Tags',
      
      // User Management
      createUser: 'Create User',
      editUser: 'Edit User',
      fullName: 'Full Name',
      email: 'Email',
      sipNumber: 'SIP Number',
      permissions: 'Permissions',
      queueAccess: 'Queue Access',
      isAdmin: 'Administrator',
      canViewKPI: 'View KPI',
      canManageQueues: 'Manage Queues',
      canEditTags: 'Edit Tags',
      canManageUsers: 'Manage Users',
      
      // Time format
      seconds: 'sec',
      minutes: 'min',
      hours: 'hr'
    }
  };

  const t = (key) => translations[language][key] || key;

  // Removed dragging state - phone is now a static bottom bar

  // Queue State
  const [queues, setQueues] = useState({});
  const [allQueues, setAllQueues] = useState([]); // All queues for transfer (not filtered by permissions)
  const [myQueueMemberships, setMyQueueMemberships] = useState([]);
  const [showQueueManager, setShowQueueManager] = useState(false);

  // CDR State
  const [cdrData, setCdrData] = useState({ data: [], total: 0 });
  const [filters, setFilters] = useState({
    queue: '',
    startDate: '',
    endDate: '',
    phone: '',
    agent: '',
    direction: '',
    status: '',
    search: '',
    limit: 50,
    offset: 0
  });
  const [selectedCall, setSelectedCall] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  
  // Category states
  const [categories, setCategories] = useState({ lvl1: [], lvl2: [], lvl3: [] });
  const [callTypes, setCallTypes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({
    lvl1: null,
    lvl2: null,
    lvl3: null,
    callType: null
  });
  const [permissions, setPermissions] = useState({
    can_view_kpi: false,
    can_manage_queues: false,
    can_edit_tags: false,
    can_manage_users: false,
    is_admin: false
  });

  const [users, setUsers] = useState([]);
  const [availableQueuesForAssignment, setAvailableQueuesForAssignment] = useState([]);
  const [availableSipNumbers, setAvailableSipNumbers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    ldap_username: '',
    sip_number: '',
    full_name: '',
    email: '',
    queues: [],
    viewableQueues: [],
    permissions: {
      can_view_kpi: false,
      can_manage_queues: false,
      can_edit_tags: false,
      can_manage_users: false,
      is_admin: false
    }
  });

  // Removed drag handlers - phone is now a static bottom bar



  // Add to data loading functions:
  const loadUsers = useCallback(async () => {
    try {
      const startTime = performance.now();
      const usersList = await apiService.getUsers();
      recordMetric('load_users', performance.now() - startTime, { userCount: usersList.length });
      setUsers(usersList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  const loadAvailableQueuesForAssignment = useCallback(async () => {
    try {
      const queues = await apiService.getAvailableQueues();
      setAvailableQueuesForAssignment(queues);
    } catch (error) {
      console.error('Failed to load queues:', error);
    }
  }, []);

  const loadAvailableSipNumbers = useCallback(async () => {
    try {
      const sipNumbers = await apiService.getAvailableSipNumbers();
      setAvailableSipNumbers(sipNumbers);
    } catch (error) {
      console.error('Failed to load SIP numbers:', error);
    }
  }, []);

  // User Management Functions
  const handleCreateUser = async () => {
    try {
      const startTime = performance.now();
      await apiService.createUser(userForm);
      recordMetric('create_user', performance.now() - startTime, { username: userForm.ldap_username });
      alert('✅ User created successfully');
      setShowUserModal(false);
      resetUserForm();
      loadUsers();
    } catch (error) {
      alert('❌ Failed to create user: ' + error.message);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const startTime = performance.now();
      // Check if SIP number is changing
      if (editingUser && userForm.sip_number !== editingUser.sip_number) {
        const confirmed = window.confirm(
          `⚠️ WARNING: You are about to change the SIP number from ${editingUser.sip_number} to ${userForm.sip_number}.\n\n` +
          `This will:\n` +
          `• Disconnect any active calls\n` +
          `• Require the user to re-login with new credentials\n` +
          `• Automatically migrate queue memberships\n` +
          `• Automatically migrate missed call notifications\n` +
          `• Automatically migrate notification preferences\n\n` +
          `Historical call records (CDR) will keep the original SIP number for accuracy.\n\n` +
          `Are you sure you want to continue?`
        );
        
        if (!confirmed) {
          return;
        }
      }
      
      await apiService.updateUser(editingUser.ldap_username, {
        full_name: userForm.full_name,
        email: userForm.email,
        sip_number: userForm.sip_number,
        permissions: userForm.permissions,
        queues: userForm.queues,
        viewableQueues: userForm.viewableQueues
      });
      
      recordMetric('update_user', performance.now() - startTime, { username: editingUser.ldap_username });
      
      if (editingUser && userForm.sip_number !== editingUser.sip_number) {
        alert('✅ User updated successfully!\n\nSIP number changed. The user must re-login with the new SIP credentials.');
      } else {
        alert('✅ User updated successfully');
      }
      
      setShowUserModal(false);
      resetUserForm();
      loadUsers();
    } catch (error) {
      alert('❌ Failed to update user: ' + error.message);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to deactivate user ${username}?`)) {
      return;
    }

    try {
      const startTime = performance.now();
      await apiService.deleteUser(username);
      recordMetric('delete_user', performance.now() - startTime, { username });
      alert('✅ User deactivated');
      loadUsers();
    } catch (error) {
      alert('❌ Failed to deactivate user: ' + error.message);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      ldap_username: '',
      sip_number: '',
      full_name: '',
      email: '',
      queues: [],
      viewableQueues: [],
      permissions: {
        can_view_kpi: false,
        can_manage_queues: false,
        can_edit_tags: false,
        can_manage_users: false,
        is_admin: false
      }
    });
    setEditingUser(null);
  };

  const openEditUserModal = (user) => {
    setEditingUser(user); // Store the full user object, not just the username
    setUserForm({
      ldap_username: user.ldap_username,
      sip_number: user.sip_number,
      full_name: user.full_name || '',
      email: user.email || '',
      queues: user.allowed_queues || [],
      viewableQueues: user.viewable_queues || [],
      permissions: {
        can_view_kpi: user.can_view_kpi || false,
        can_manage_queues: user.can_manage_queues || false,
        can_edit_tags: user.can_edit_tags || false,
        can_manage_users: user.can_manage_users || false,
        is_admin: user.is_admin || false
      }
    });
    // Load available SIP numbers for editing
    loadAvailableQueuesForAssignment();
    loadAvailableSipNumbers();
    setShowUserModal(true);
  };

  // CSV Import State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importStatus, setImportStatus] = useState(null);

  // Transfer State
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferType, setTransferType] = useState('queue');

  // Statistics State
  const [agentStats, setAgentStats] = useState(null);
  const [overallStats, setOverallStats] = useState(null);
  const [hourlyStats, setHourlyStats] = useState([]);

  // Call History State
  const [callHistory, setCallHistory] = useState([]);
  //const [showCallHistory, setShowCallHistory] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const sipSessionRef = useRef(null);
  const sipUARef = useRef(null);
  const callTimerRef = useRef(null);
  const holdTimerRef = useRef(null); // FIX: Added missing ref for hold timer
  const audioContextRef = useRef(null);
  const toneTimeoutRef = useRef(null);

  // Constants
  const SESSION_KEY = 'comfy_sip_session';
  const SESSION_EXPIRY = 12 * 60 * 60 * 1000; // 12 hours
  const [showDialPad, setShowDialPad] = useState(false);
  const [showCallHistorySidebar, setShowCallHistorySidebar] = useState(false);
  const [incomingCallNotes, setIncomingCallNotes] = useState('');
  const [incomingCallTags, setIncomingCallTags] = useState([]);
  const [activeCallCard, setActiveCallCard] = useState(null); // Track active call for notes/tags

  // Lookup caller information from contacts or CDR
  const lookupCallerInfo = async (phoneNumber) => {
    try {
      // Try to find contact info
      const contactInfo = await apiService.lookupContact(phoneNumber);

      if (contactInfo) {
        return {
          number: phoneNumber,
          name: contactInfo.full_name || phoneNumber,
          company: contactInfo.company,
          email: contactInfo.email,
          notes: contactInfo.notes,
          lastCallDate: contactInfo.last_call_date,
          lastCallStatus: contactInfo.last_call_status,
          fromCDR: contactInfo.from_cdr || false
        };
      }

      return { number: phoneNumber, name: phoneNumber };
    } catch (error) {
      console.error('Caller lookup failed:', error);
      return { number: phoneNumber, name: phoneNumber };
    }
  };

  // Add state for KPI report
  const [kpiReport, setKpiReport] = useState([]);
  const [kpiFilters, setKpiFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    queue: ''
  });

  // Session Management
  const encodeData = (data) => {
    return btoa(encodeURIComponent(JSON.stringify(data)).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode('0x' + p1);
    }));
  };

  const decodeData = (encodedData) => {
    return JSON.parse(decodeURIComponent(atob(encodedData).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));
  };

  const saveSession = (userData, userPermissions) => {
    const session = {
      userData: encodeData(userData),
      permissions: encodeData(userPermissions),
      timestamp: Date.now(),
      expiresAt: Date.now() + SESSION_EXPIRY
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  };

  const loadSession = () => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return null;

      const session = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) {
        localStorage.removeItem(SESSION_KEY);
        return null;
      }

      return {
        userData: decodeData(session.userData),
        permissions: session.permissions ? decodeData(session.permissions) : null
      };
    } catch (error) {
      console.error('Failed to load session:', error);
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  };

  const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
  };

  // Force cleanup stale call state - DEFINED EARLY TO AVOID TEMPORAL DEAD ZONE
  const forceCleanupCallState = useCallback(() => {
    console.log('🧹 Force cleaning up call state');
    
    // Stop all timers
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    
    // Reset all call-related state
    setInCall(false);
    setCallStatus('');
    setCallDuration(0);
    setIncomingCall(null);
    setCallerInfo(null);
    setOnHold(false);
    setHoldStartTime(null);
    setCurrentHoldDuration(0);
    setCallStartTime(null);
    setTotalHoldDuration(0);
    setHoldCount(0);
    
    // Stop tones
    stopAllTones();
    
    // Clear SIP session
    if (sipSessionRef.current) {
      try {
        if (!sipSessionRef.current.isEnded()) {
          sipSessionRef.current.terminate();
        }
      } catch (error) {
        console.log('Error terminating SIP session:', error);
      }
      sipSessionRef.current = null;
    }
    
    console.log('✅ Call state cleaned up');
  }, []);

  // Constants
  const CALL_HISTORY_KEY = 'comfy_call_history';

  // Audio Context
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        console.log('🔊 Audio context initialized');
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    }
  };

  // Check Microphone Status
  const checkMicrophoneStatus = async () => {
    try {
      setMicrophoneStatus({ available: false, checking: true, error: null });
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support microphone access');
      }

      // Try to get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Success - microphone is available
      console.log('🎤 Microphone access granted');
      setMicrophoneStatus({ available: true, checking: false, error: null });
      setShowMicWarning(false);
      
      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (error) {
      console.error('🎤 Microphone access error:', error);
      
      let errorMessage = 'Microphone not available';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone permission denied. Please allow microphone access in your browser settings.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = 'Microphone is being used by another application.';
      }
      
      setMicrophoneStatus({ available: false, checking: false, error: errorMessage });
      setShowMicWarning(true);
      
      return false;
    }
  };

  // Generate Tones
  const generateTone = (frequency, duration = 0.5) => {
    if (!audioContextRef.current) {
      initAudioContext();
    }

    if (audioContextRef.current && audioContextRef.current.state === 'running') {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);

      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);

      return oscillator;
    }
    return null;
  };

  const playTone = (toneType) => {
    if (toneTimeoutRef.current) {
      clearInterval(toneTimeoutRef.current);
      toneTimeoutRef.current = null;
    }

    setTimeout(() => {
      try {
        if (toneType === 'dial') {
          const playDialTone = () => {
            generateTone(350, 0.5);
            generateTone(440, 0.5);
          };
          playDialTone();
          toneTimeoutRef.current = setInterval(playDialTone, 500);
        } else if (toneType === 'ringback') {
          // Ringback tone for outbound calls (standard phone ringing sound)
          const playRingbackTone = () => {
            generateTone(440, 2);  // 2 seconds on
            setTimeout(() => {
              // 4 seconds off (silence)
            }, 2000);
          };
          playRingbackTone();
          toneTimeoutRef.current = setInterval(playRingbackTone, 6000); // Repeat every 6 seconds
        } else if (toneType === 'ring') {
          // Use custom ringtone for INCOMING calls only
          if (customRingtone && ringtoneRef.current) {
            // Set source and properties
            ringtoneRef.current.src = customRingtone;
            ringtoneRef.current.volume = ringtoneVolume;
            ringtoneRef.current.loop = true;
            
            // Add error handler for missing file
            ringtoneRef.current.onerror = () => {
              console.error('❌ Ringtone file failed to load:', customRingtone);
              // Fallback to generated tone
              playGeneratedRingtone();
            };
            
            // Load the audio first
            ringtoneRef.current.load();
            
            // Wait for canplay event before playing
            ringtoneRef.current.oncanplay = () => {
              ringtoneRef.current.play().catch(e => {
                console.error('Custom ringtone play error:', e);
                // Fallback to generated tone
                playGeneratedRingtone();
              });
            };
          } else {
            playGeneratedRingtone();
          }
        }
      } catch (error) {
        console.log('Tone error:', error);
      }
    }, 50);
  };

  const playGeneratedRingtone = () => {
    const playRingTone = () => {
      generateTone(440, 2);
      generateTone(480, 2);
    };
    playRingTone();
    toneTimeoutRef.current = setInterval(playRingTone, 6000);
  };

  const stopAllTones = () => {
    console.log('🔇 Stopping all tones...');
    
    // FIX #7: Stop interval timers
    if (toneTimeoutRef.current) {
      clearInterval(toneTimeoutRef.current);
      toneTimeoutRef.current = null;
    }
    
    // FIX #7: Stop custom ringtone completely
    if (ringtoneRef.current) {
      try {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current.loop = false; // Disable loop
        ringtoneRef.current.src = ''; // Clear source to fully stop
        // Remove event handlers to prevent re-triggering
        ringtoneRef.current.oncanplay = null;
        ringtoneRef.current.onerror = null;
        console.log('✅ Ringtone stopped');
      } catch (error) {
        console.error('Error stopping ringtone:', error);
      }
    }
    
    // FIX #7: Stop all oscillators by suspending audio context briefly
    if (audioContextRef.current && audioContextRef.current.state === 'running') {
      try {
        // Suspend and resume to kill all active oscillators
        audioContextRef.current.suspend().then(() => {
          audioContextRef.current.resume();
          console.log('✅ Audio context reset');
        });
      } catch (error) {
        console.error('Error resetting audio context:', error);
      }
    }
  };

  const handleRingtoneUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('audio/')) {
        alert('Please select an audio file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setCustomRingtone(dataUrl);
        localStorage.setItem('customRingtone', dataUrl);
        alert('✅ Custom ringtone uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRingtoneVolumeChange = (volume) => {
    setRingtoneVolume(volume);
    localStorage.setItem('ringtoneVolume', volume.toString());
  };

  const handleRemoveRingtone = () => {
    setCustomRingtone('/sounds/ringtone.mp3'); // Reset to default
    localStorage.removeItem('customRingtone');
    alert('✅ Custom ringtone removed, using default');
  };

  // Call History Management
  const saveCallToHistory = (callData) => {
    const history = JSON.parse(localStorage.getItem(CALL_HISTORY_KEY) || '[]');
    const newCall = {
      id: Date.now(),
      number: callData.number,
      type: callData.type,
      timestamp: new Date().toISOString(),
      duration: callData.duration || 0,
      status: callData.status
    };

    history.unshift(newCall);
    if (history.length > 50) {
      history.splice(50);
    }

    localStorage.setItem(CALL_HISTORY_KEY, JSON.stringify(history));
    setCallHistory(history);
  };

  const loadCallHistory = useCallback(() => {
    const history = JSON.parse(localStorage.getItem(CALL_HISTORY_KEY) || '[]');
    setCallHistory(history);
  }, []);

  // SIP Connection
  const connectSIP = (sipNum, sipPass) => {
    console.log(`Connecting to SIP: sip:${sipNum}@sip1.comfy.org.ua`);

    const socket = new JsSIP.WebSocketInterface(
      process.env.REACT_APP_WSS_URL || ''
    );

    const configuration = {
      sockets: [socket],
      uri: `sip:${sipNum}@sip1.comfy.org.ua`,
      password: sipPass,
      display_name: user?.ldapUser || sipNum,
      session_timers: false,
      register: true
    };

    const ua = new JsSIP.UA(configuration);

    ua.on('connected', () => console.log('✅ WebSocket connected'));

    ua.on('registered', () => {
      console.log('✅ SIP registered');
      setSipConnected(true);
    });

    ua.on('registrationFailed', (e) => {
      console.error('❌ Registration failed:', e);
      setSipConnected(false);
      alert('SIP registration failed. Please check your credentials.');
    });

    ua.on('unregistered', () => {
      console.log('⚠️  SIP unregistered');
      setSipConnected(false);
    });

    ua.on('newRTCSession', async (data) => {
      const session = data.session;

      if (session.direction === 'incoming') {
        const callerNum = session.remote_identity.uri.user;
        const callerName = session.remote_identity.display_name || callerNum;

        console.log('📞 Incoming call from:', callerNum);

        // Lookup caller information from database
        const callerDetails = await lookupCallerInfo(callerNum);

        setCallerInfo({
          number: callerNum,
          name: callerDetails.name !== callerNum ? callerDetails.name : callerName,
          lastCallDate: callerDetails.lastCallDate,
          lastCallStatus: callerDetails.lastCallStatus,
          fromCDR: callerDetails.fromCDR
        });

        setCallNumber(callerNum);
        setIncomingCall(session);

        playTone('ring');

        // Show system-wide browser notification
        const displayName = callerDetails.name !== callerNum ? callerDetails.name : callerName;
        showIncomingCallNotification(displayName, callerNum);

        session.on('accepted', () => handleIncomingAccepted(session));
        session.on('ended', () => handleEndCall());
        session.on('failed', () => handleEndCall());
      }

      sipSessionRef.current = session;
    });

    ua.start();
    sipUARef.current = ua;
  };

  const handleIncomingAccepted = (session) => {
    // FIX #7: Double-check ringtone is stopped (safety measure)
    stopAllTones();
    console.log('✅ Call connected - ensuring all tones stopped');
    
    setInCall(true);
    setIncomingCall(null);
    setCallDuration(0);
    setCallStatus('connected');
    
    // Initialize mute state to unmuted when call starts
    setMuted(false);
    console.log('🎤 Microphone initialized as unmuted');
    
    // Track call start time for CDR matching
    const startTime = new Date();
    setCallStartTime(startTime);

    // Keep the call card open with current caller info
    setActiveCallCard({
      number: callerInfo?.number || callNumber,
      name: callerInfo?.name || callNumber,
      direction: 'incoming',
      startTime: startTime,
      tags: incomingCallTags,
      notes: incomingCallNotes
    });

    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    // Setup remote audio with improved retry logic
    setupRemoteAudio(session);
  };

  // Helper function to attach stream to audio element
  const attachStreamToAudio = useCallback((remoteStream) => {
    if (!remoteAudioRef.current) {
      console.error('❌ remoteAudioRef.current is null!');
      return;
    }
    
    console.log('✅ Attaching remote stream to audio element');
    remoteAudioRef.current.srcObject = remoteStream;
    remoteAudioRef.current.volume = 1.0;
    remoteAudioRef.current.muted = false;
    
    // Explicitly set audio output device to default (Windows 11 23H2 fix)
    if (remoteAudioRef.current.setSinkId) {
      remoteAudioRef.current.setSinkId('')
        .then(() => {
          console.log('✅ Audio output device set to default');
        })
        .catch(error => {
          console.warn('⚠️ setSinkId failed:', error.message);
        });
    }
    
    // Play audio
    setTimeout(() => {
      playRemoteAudio().catch(error => {
        console.error('❌ Failed to play remote audio:', error);
      });
    }, 50);
  }, []);

  // Setup remote audio - EXACT pattern from working CRM (SIP.js style)
  // This fixes Windows 11 23H2 audio routing issues
  const setupRemoteAudio = useCallback((session) => {
    console.log('🔊 Setting up remote audio (SIP.js pattern)...');
    
    if (!session || !session.connection) {
      console.error('❌ Invalid session or connection');
      return;
    }
    
    // CRITICAL FIX: Call getReceivers() SYNCHRONOUSLY, not with setTimeout
    // This is the EXACT pattern used by the working CRM with SIP.js
    const pc = session.connection;
    const remoteStream = new MediaStream();
    
    console.log('📡 Getting receivers synchronously...');
    const receivers = pc.getReceivers();
    console.log(`Found ${receivers.length} receivers`);
    
    // Add all tracks to remote stream
    receivers.forEach((receiver) => {
      if (receiver.track) {
        console.log(`🎵 Adding ${receiver.track.kind} track:`, {
          id: receiver.track.id,
          label: receiver.track.label,
          enabled: receiver.track.enabled,
          muted: receiver.track.muted,
          readyState: receiver.track.readyState
        });
        remoteStream.addTrack(receiver.track);
      }
    });
    
    // Check if we got audio tracks
    const audioTracks = remoteStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn('⚠️ No audio tracks found, will retry with track event...');
      
      // Fallback: Use track event if no tracks available yet
      const handleTrack = (event) => {
        console.log('🎵 Track event fired:', event.track.kind);
        if (event.track.kind === 'audio') {
          remoteStream.addTrack(event.track);
          attachStreamToAudio(remoteStream);
          pc.removeEventListener('track', handleTrack);
        }
      };
      pc.addEventListener('track', handleTrack);
      return;
    }
    
    console.log(`✅ Found ${audioTracks.length} audio track(s)`);
    attachStreamToAudio(remoteStream);
  }, [attachStreamToAudio]);

  // Play remote audio with Safari/Firefox support
  const playRemoteAudio = async () => {
    if (!remoteAudioRef.current) {
      console.error('❌ Remote audio ref is null');
      return;
    }

    console.log('🔊 Attempting to play remote audio...');
    console.log('Audio element state:', {
      srcObject: !!remoteAudioRef.current.srcObject,
      paused: remoteAudioRef.current.paused,
      muted: remoteAudioRef.current.muted,
      volume: remoteAudioRef.current.volume,
      readyState: remoteAudioRef.current.readyState
    });

    try {
      // Resume audio context if suspended (Safari/Firefox/Chrome fix)
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log('🔊 Audio context resumed for remote audio');
      }

      // Ensure audio element is not muted and volume is max
      remoteAudioRef.current.muted = false;
      remoteAudioRef.current.volume = 1.0;

      // Try to play with retry logic (increased attempts for better reliability)
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          await remoteAudioRef.current.play();
          console.log('✅ Remote audio playing successfully');
          
          // Verify it's actually playing after a short delay
          setTimeout(() => {
            if (remoteAudioRef.current && remoteAudioRef.current.paused) {
              console.warn('⚠️ Audio element is paused after play() - retrying');
              remoteAudioRef.current.play().catch(e => console.error('Retry failed:', e));
            }
          }, 100);
          
          return;
        } catch (error) {
          console.warn(`⚠️ Remote audio play attempt ${attempt}/5 failed:`, error.message);
          if (attempt < 5) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      console.error('❌ Failed to play remote audio after 5 attempts');
      console.error('This may be due to browser autoplay policy. User interaction may be required.');
      
    } catch (error) {
      console.error('❌ Remote audio setup error:', error);
    }
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        console.log('🔔 Notification permission:', permission);
        return permission === 'granted';
      } catch (error) {
        console.error('Notification permission error:', error);
        return false;
      }
    }
    return Notification.permission === 'granted';
  };

  // Show browser notification for incoming call
  const showIncomingCallNotification = (callerName, callerNumber) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification('📞 Incoming Call', {
          body: `${callerName}\n${callerNumber}`,
          icon: '/logo192.png', // Use your app icon
          badge: '/logo192.png',
          tag: 'incoming-call', // Replace previous notification
          requireInteraction: true, // Keep notification until user interacts
          silent: false, // Play system sound
        });

        // Focus window when notification is clicked
        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        // Auto-close notification after 30 seconds
        setTimeout(() => {
          notification.close();
        }, 30000);

        return notification;
      } catch (error) {
        console.error('Failed to show notification:', error);
      }
    }
    return null;
  };

  // Authentication
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Initialize audio context on user interaction (required by browser autoplay policy)
      initAudioContext();
      
      // Request notification permission
      await requestNotificationPermission();
      
      // Preload ringtone audio
      if (ringtoneRef.current && customRingtone) {
        ringtoneRef.current.src = customRingtone;
        ringtoneRef.current.load();
        // Try to play and immediately pause to unlock audio
        ringtoneRef.current.play().then(() => {
          ringtoneRef.current.pause();
          ringtoneRef.current.currentTime = 0;
          console.log('🔊 Ringtone preloaded and ready');
        }).catch(e => {
          console.log('Ringtone preload attempt:', e.message);
        });
      }

      const startTime = performance.now();
      const response = await apiService.login(loginForm.username, loginForm.password);
      recordMetric('login', performance.now() - startTime, { method: response.authMethod });
      
      if (response.authMethod) {
        console.log(`✅ Authenticated via ${response.authMethod}`);
      }

      const userPermissions = response.permissions || {
        can_view_kpi: false,
        can_manage_queues: false,
        can_edit_tags: false,
        can_manage_users: false,
        is_admin: false
      };

      const userData = {
        ldapUser: response.ldapUser,
        sipNumber: response.sipNumber,
        sipPassword: response.sipPassword,
        allowedQueues: response.allowedQueues,
        viewableQueues: response.viewableQueues || [],
        authMethod: response.authMethod
      };

      setUser(userData);
      setPermissions(userPermissions);
      setIsLoggedIn(true);

      // Save session with permissions
      saveSession(userData, userPermissions);

      connectSIP(response.sipNumber, response.sipPassword);

      loadQueues();
      loadAllQueues(); // Load all queues for transfer dropdown
      loadCDR();
      loadCallHistory();
      loadMyQueueMemberships();
      loadAgentStats();

      // Load tags - all users can view tags to apply them to calls
      loadTags();

      // Load users if has permission
      if (userPermissions.can_manage_users || userPermissions.is_admin) {
        loadUsers();
      }
    } catch (error) {
      alert('Login failed: ' + error.message);
    }
  };

  const handleLogout = () => {
    if (sipUARef.current) {
      sipUARef.current.stop();
      sipUARef.current = null;
    }

    setIsLoggedIn(false);
    setSipConnected(false);
    setUser(null);
    setQueues({});
    setCdrData({ data: [], total: 0 });
    setMyQueueMemberships([]);
    setAgentStats(null);

    if (inCall) handleEndCall();

    // Clear session
    clearSession();

    apiService.logout();
  };

  // Data Loading Functions
const loadQueues = useCallback(async () => {
  try {
    const startTime = performance.now();
    const stats = await apiService.getQueueStats();
    recordMetric('load_queues', performance.now() - startTime, { queueCount: Object.keys(stats).length });
    setQueues(stats);
  } catch (error) {
    console.error('Failed to load queues:', error);
  }
}, []);

const loadAllQueues = useCallback(async () => {
  try {
    const startTime = performance.now();
    const allQueuesData = await apiService.getAllQueues();
    recordMetric('load_all_queues', performance.now() - startTime, { queueCount: allQueuesData.length });
    setAllQueues(allQueuesData);
  } catch (error) {
    console.error('Failed to load all queues:', error);
  }
}, []);

const loadCDR = useCallback(async () => {
  try {
    const startTime = performance.now();
    const records = await apiService.getCDRRecords(filters);
    recordMetric('load_cdr', performance.now() - startTime, { recordCount: records.data?.length || 0 });
    setCdrData(records);
  } catch (error) {
    console.error('Failed to load CDR:', error);
  }
}, [filters]);

const loadTags = useCallback(async () => {
  try {
    const startTime = performance.now();
    const tags = await apiService.getTags();
    recordMetric('load_tags', performance.now() - startTime, { tagCount: tags.length });
    setAvailableTags(tags);
  } catch (error) {
    console.error('Failed to load tags:', error);
  }
}, []);

const loadMyQueueMemberships = useCallback(async () => {
  try {
    const startTime = performance.now();
    const memberships = await apiService.getMyQueueMemberships();
    recordMetric('load_queue_memberships', performance.now() - startTime, { membershipCount: memberships.length });
    setMyQueueMemberships(memberships);
  } catch (error) {
    console.error('Failed to load queue memberships:', error);
  }
}, []);

const loadAgentStats = useCallback(async () => {
  if (!user?.sipNumber) return;
  try {
    const startTime = performance.now();
    const stats = await apiService.getAgentStatistics(user.sipNumber);
    recordMetric('load_agent_stats', performance.now() - startTime, { statsCount: stats?.length || 0 });
    setAgentStats(stats);
  } catch (error) {
    console.error('Failed to load agent statistics:', error);
  }
}, [user?.sipNumber]);

const loadOverallStats = useCallback(async () => {
  try {
    const startTime = performance.now();
    const stats = await apiService.getOverallStatistics(filters.startDate, filters.endDate, filters.queue);
    setOverallStats(stats);

    const hourly = await apiService.getHourlyStatistics(filters.startDate, filters.endDate, filters.queue);
    recordMetric('load_overall_stats', performance.now() - startTime, { hourlyCount: hourly.length });
    setHourlyStats(hourly);
  } catch (error) {
    console.error('Failed to load overall statistics:', error);
  }
}, [filters.startDate, filters.endDate, filters.queue]);

const loadKPIReport = useCallback(async () => {
  try {
    const startTime = performance.now();
    const report = await apiService.getAgentKPIReport(
      kpiFilters.startDate,
      kpiFilters.endDate,
      kpiFilters.queue
    );
    recordMetric('load_kpi_report', performance.now() - startTime, { reportCount: report.length });
    setKpiReport(report);
  } catch (error) {
    console.error('Failed to load KPI report:', error);
  }
}, [kpiFilters.startDate, kpiFilters.endDate, kpiFilters.queue]);

  // Queue Management
  const handleQueueLogin = async (queueName) => {
    try {
      const startTime = performance.now();
      await apiService.loginToQueue(queueName);
      recordMetric('queue_login', performance.now() - startTime, { queue: queueName });
      alert(`✅ Logged into ${queueName}`);
      loadMyQueueMemberships();
      loadQueues();
    } catch (error) {
      alert(`❌ Failed: ${error.message}`);
    }
  };

  const handleQueueLogout = async (queueName) => {
    try {
      const startTime = performance.now();
      await apiService.logoutFromQueue(queueName);
      recordMetric('queue_logout', performance.now() - startTime, { queue: queueName });
      alert(`✅ Logged out from ${queueName}`);
      loadMyQueueMemberships();
      loadQueues();
    } catch (error) {
      alert(`❌ Failed: ${error.message}`);
    }
  };

  const handleQueuePause = async (queueName, paused) => {
    try {
      const startTime = performance.now();
      await apiService.setQueuePause(queueName, paused);
      recordMetric('queue_pause', performance.now() - startTime, { queue: queueName, paused });
      alert(paused ? `⏸️ Paused in ${queueName}` : `▶️ Unpaused in ${queueName}`);
      loadMyQueueMemberships();
      loadQueues();
    } catch (error) {
      alert(`❌ Failed: ${error.message}`);
    }
  };

  const handleRemoveAgentFromQueue = async (queueName, agentSip, agentName) => {
    if (!window.confirm(`Remove ${agentName || agentSip} from ${queueName}?`)) {
      return;
    }

    try {
      await apiService.removeAgentFromQueue(queueName, agentSip);
      alert(`✅ Agent ${agentName || agentSip} removed from ${queueName}`);
      loadQueues();
    } catch (error) {
      alert(`❌ Failed: ${error.message}`);
    }
  };

  // Phone Number Normalization
  const normalizePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters (spaces, dashes, parentheses, etc.)
    let normalized = phoneNumber.replace(/\D/g, '');
    
    // Short numbers (extensions, service numbers) - don't normalize
    // Examples: 1777, 913456, 6900, *45, etc.
    if (normalized.length <= 6) {
      console.log(`📱 Short number (not normalized): ${phoneNumber} → ${normalized}`);
      return normalized;
    }
    
    // Only normalize full phone numbers (7+ digits)
    // If starts with 380 (Ukraine country code), convert to 0
    if (normalized.startsWith('380') && normalized.length >= 12) {
      normalized = '0' + normalized.substring(3);
    }
    
    // If starts with +380, convert to 0
    if (phoneNumber.startsWith('+380')) {
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      if (digitsOnly.length >= 12) {
        normalized = '0' + digitsOnly.substring(3);
      }
    }
    
    console.log(`📱 Normalized phone: ${phoneNumber} → ${normalized}`);
    return normalized;
  };

  // Call Handling
  const handleCall = () => {
    if (!callNumber || !sipUARef.current) return;

    // Normalize phone number before calling
    const normalizedNumber = normalizePhoneNumber(callNumber);
    if (!normalizedNumber) {
      console.error('❌ Invalid phone number');
      return;
    }

    const callStartTime = performance.now();
    initAudioContext();
    console.log(`📞 Calling: ${callNumber} → ${normalizedNumber}`);

    setCallStatus('dialing');
    playTone('dial');

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: [] }
    };

    try {
      const session = sipUARef.current.call(normalizedNumber, options);
      sipSessionRef.current = session;

      session.on('connecting', () => {
        console.log('📶 Call connecting...');
        setInCall(true);
        setCallStatus('dialing');
      });

      session.on('progress', () => {
        console.log('📞 Call ringing...');
        setCallStatus('ringing');
        setTimeout(() => {
          stopAllTones();
          playTone('ringback'); // Use ringback tone for outbound calls
        }, 100);
      });

      session.on('accepted', () => {
        console.log('✅ Call accepted');
        recordMetric('call_setup_time', performance.now() - callStartTime, { number: normalizedNumber });
        stopAllTones();
        setInCall(true);
        setCallDuration(0);
        setCallStatus('connected');
        
        // Initialize mute state to unmuted when call starts
        setMuted(false);
        console.log('🎤 Microphone initialized as unmuted');
        
        // Track call start time for CDR matching
        const startTime = new Date();
        setCallStartTime(startTime);

        // Open call card for outgoing call
        setActiveCallCard({
          number: callNumber,
          name: callNumber,
          direction: 'outgoing',
          startTime: startTime,
          tags: [],
          notes: ''
        });

        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);

        saveCallToHistory({
          number: callNumber,
          type: 'outgoing',
          status: 'completed'
        });

        // Setup remote audio with improved retry logic
        setupRemoteAudio(session);
      });

      session.on('ended', () => handleEndCall());
      session.on('failed', (e) => {
        console.log('❌ Call failed:', e);
        stopAllTones();

        saveCallToHistory({
          number: callNumber,
          type: 'outgoing',
          status: 'failed'
        });

        handleEndCall();

        const cause = e.cause || 'Unknown error';
        if (cause === 'Busy') {
          alert('Line is busy. Please try again.');
        } else if (cause === 'Rejected') {
          alert('Call was rejected.');
        } else if (cause === 'Not Found') {
          alert('Number not found or unavailable.');
        }
      });

    } catch (error) {
      console.error('Error initiating call:', error);
      alert('Failed to initiate call: ' + error.message);
      setInCall(false);
      setCallStatus('');
      stopAllTones();
    }
  };

  // Advanced Connection Quality Monitoring
  const checkConnectionQuality = useCallback(async () => {
    if (!sipSessionRef.current || !inCall) return;

    try {
      const overallStartTime = performance.now();
      
      // Multiple health checks for better accuracy
      const pings = [];
      for (let i = 0; i < 3; i++) {
        const pingStart = Date.now();
        try {
          const healthCheck = await apiService.healthCheck();
          const pingRtt = Date.now() - pingStart;
          
          if (healthCheck && healthCheck.status !== 'error') {
            pings.push(pingRtt);
          }
        } catch (error) {
          // Health check failed, don't add to pings
          console.warn('Health check failed:', error.message);
        }
        
        // Small delay between pings
        if (i < 2) await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (pings.length > 0) {
        // Calculate metrics
        const avgRtt = pings.reduce((a, b) => a + b, 0) / pings.length;
        const minRtt = Math.min(...pings);
        const maxRtt = Math.max(...pings);
        const jitter = maxRtt - minRtt;
        const packetLoss = ((3 - pings.length) / 3) * 100;
        
        // Determine status based on multiple factors
        let status = 'good';
        let score = 100;
        
        // RTT impact
        if (avgRtt > 500) {
          status = 'poor';
          score -= 40;
        } else if (avgRtt > 200) {
          status = 'fair';
          score -= 20;
        } else if (avgRtt > 100) {
          score -= 10;
        }
        
        // Jitter impact
        if (jitter > 100) {
          score -= 20;
          if (status === 'good') status = 'fair';
        } else if (jitter > 50) {
          score -= 10;
        }
        
        // Packet loss impact
        if (packetLoss > 0) {
          score -= packetLoss * 2;
          if (packetLoss > 10 && status !== 'poor') status = 'poor';
        }
        
        score = Math.max(0, Math.min(100, score));
        
        setConnectionQuality(prev => {
          const newHistory = [...prev.history, { rtt: avgRtt, jitter, packetLoss, score, timestamp: Date.now() }];
          // Keep only last 10 measurements
          if (newHistory.length > 10) newHistory.shift();
          
          return {
            ...prev,
            status,
            rtt: Math.round(avgRtt),
            jitter: Math.round(jitter),
            packetLoss: Math.round(packetLoss),
            score: Math.round(score),
            history: newHistory,
            lastCheck: new Date()
          };
        });
        
        // Analyze trends
        setConnectionQuality(prevQuality => {
          if (prevQuality.history.length < 3) return prevQuality; // Need at least 3 measurements
          
          const recent = prevQuality.history.slice(-3);
          const rtts = recent.map(h => h.rtt);
          
          // Calculate trends
          const rttTrend = rtts[2] - rtts[0]; // Change over last 3 measurements
          const rttVariance = Math.max(...rtts) - Math.min(...rtts);
          
          let recommendation = null;
          let degrading = false;
          let improving = false;
          let unstable = false;
          
          // Analyze patterns
          if (rttTrend > 100) {
            degrading = true;
            recommendation = 'Connection degrading - consider moving closer to WiFi';
          } else if (rttTrend < -50) {
            improving = true;
            recommendation = 'Connection improving';
          }
          
          if (rttVariance > 200) {
            unstable = true;
            recommendation = recommendation || 'Connection unstable - avoid moving during call';
          }
          
          setNetworkTrends({
            degrading,
            improving,
            unstable,
            recommendation
          });
          
          return prevQuality;
        });
        
        // Record performance metric for connection quality check
        recordMetric('connection_quality_check', performance.now() - overallStartTime, { 
          status, 
          score, 
          successfulPings: pings.length 
        });
        
      } else {
        // All health checks failed - use basic network detection
        const isOnline = navigator.onLine;
        const status = isOnline ? 'fair' : 'disconnected';
        const score = isOnline ? 50 : 0; // Assume fair quality if online but can't reach API
        
        setConnectionQuality(prev => ({
          ...prev,
          status,
          rtt: isOnline ? 200 : 0, // Estimate RTT if online
          packetLoss: isOnline ? 10 : 100, // Estimate packet loss
          jitter: isOnline ? 50 : 0,
          score,
          lastCheck: new Date()
        }));
        
        setNetworkTrends(prev => ({
          ...prev,
          recommendation: isOnline ? 'API connection issues - call quality may be affected' : 'Check your internet connection'
        }));
        
        // Record performance metric for fallback
        recordMetric('connection_quality_check', performance.now() - overallStartTime, { 
          status, 
          score, 
          fallback: true,
          online: isOnline
        });
      }
    } catch (error) {
      console.warn('Connection quality check failed:', error);
      
      // Use basic network status as fallback
      const isOnline = navigator.onLine;
      setConnectionQuality(prev => ({
        ...prev,
        status: isOnline ? 'fair' : 'disconnected',
        rtt: isOnline ? 150 : 0,
        jitter: isOnline ? 30 : 0,
        packetLoss: isOnline ? 5 : 100,
        score: isOnline ? 60 : 0,
        lastCheck: new Date()
      }));
      
      setNetworkTrends(prev => ({
        ...prev,
        recommendation: isOnline ? 'Connection monitoring unavailable - using basic network status' : 'Network connection lost - call may drop'
      }));
    }
  }, [inCall]);

  // Predictive Cleanup - detect issues before they become critical
  const predictiveCleanup = useCallback(() => {
    if (!inCall || !connectionQuality.history.length) return;
    
    const recentScores = connectionQuality.history.slice(-5).map(h => h.score);
    const avgScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
    
    // If quality has been consistently poor for 5 measurements, warn user
    if (avgScore < 30 && recentScores.every(score => score < 40)) {
      console.warn('🚨 Predictive cleanup: Call quality critically low');
      setNetworkTrends(prev => ({
        ...prev,
        recommendation: 'Call quality critical - consider ending call and trying again'
      }));
    }
    
    // If connection has been disconnected for 3 checks, auto-cleanup
    const recentStatuses = connectionQuality.history.slice(-3);
    if (recentStatuses.every(h => h.score === 0)) {
      console.warn('🚨 Predictive cleanup: Extended disconnection detected');
      setTimeout(() => {
        if (inCall) {
          console.log('🧹 Auto-cleanup triggered by predictive analysis');
          forceCleanupCallState();
        }
      }, 5000); // Give 5 seconds for recovery
    }
  }, [inCall, connectionQuality.history, forceCleanupCallState]);



  const handleEndCall = async () => {
    console.log('🛑 Ending call');

    // Play call end sound if enabled
    if (callEndSoundEnabled) {
      try {
        generateTone(400, 0.3);
        setTimeout(() => generateTone(300, 0.3), 150);
      } catch (error) {
        console.log('End tone error:', error);
      }
    }

    // Calculate final hold duration if still on hold
    let finalHoldDuration = totalHoldDuration;
    if (onHold && holdStartTime) {
      finalHoldDuration += Math.floor((Date.now() - holdStartTime) / 1000);
    }

    // Determine which tags and notes to save (from activeCallCard or incoming call state)
    const tagsToSave = activeCallCard?.tags || incomingCallTags;
    const notesToSave = activeCallCard?.notes || incomingCallNotes;
    
    // Save call data to CDR if we have tags, notes, or hold duration
    const hasDataToSave = tagsToSave.length > 0 || notesToSave.trim() || finalHoldDuration > 0 || holdCount > 0;
    
    if (callStartTime && hasDataToSave) {
      // Wait for Asterisk to write CDR to database (usually takes 1-3 seconds)
      console.log('⏳ Waiting for CDR to be written to database...');
      console.log(`📞 Call started at: ${callStartTime.toISOString()}`);
      console.log(`📝 Saving: ${tagsToSave.length} tags, notes: ${notesToSave ? 'yes' : 'no'}, hold: ${finalHoldDuration}s`);
      
      setTimeout(async () => {
        try {
          // Get recent calls (last 10 to handle multiple calls)
          // Don't filter by phone number - just get recent calls and match by time
          const recentCalls = await apiService.getCDRRecords({
            limit: 10,
            offset: 0
          });

          if (recentCalls.data && recentCalls.data.length > 0) {
            // Find the call that matches our start time
            const callStartTimeMs = callStartTime.getTime();
            let matchedCall = null;
            let bestTimeDiff = Infinity;
            
            console.log(`🔍 Searching for call started at ${callStartTime.toISOString()}`);
            
            for (const record of recentCalls.data) {
              const recordTime = new Date(record.call_date_utc).getTime();
              const timeDiff = Math.abs(recordTime - callStartTimeMs) / 1000; // seconds
              
              console.log(`  - CDR ${record.id}: ${record.call_date_utc}, diff: ${timeDiff.toFixed(1)}s`);
              
              // Find the call with the closest start time (within 30 seconds)
              if (timeDiff < 30 && timeDiff < bestTimeDiff) {
                matchedCall = record;
                bestTimeDiff = timeDiff;
              }
            }
            
            if (matchedCall) {
              console.log(`✅ Found matching CDR record: ID ${matchedCall.id}, time diff: ${bestTimeDiff.toFixed(1)}s`);
              
              // Prepare update data
              const updateData = {};
              
              if (tagsToSave.length > 0) {
                updateData.tags = tagsToSave;
                console.log(`🏷️  Saving ${tagsToSave.length} tags: ${tagsToSave.join(', ')}`);
              }
              
              if (finalHoldDuration > 0 || holdCount > 0) {
                updateData.hold_duration = finalHoldDuration;
                updateData.hold_count = holdCount;
                console.log(`⏸️  Saving hold data: ${finalHoldDuration}s (${holdCount} times) to CDR ID: ${matchedCall.id}`);
              }
              
              // Update CDR with tags and hold duration
              if (Object.keys(updateData).length > 0) {
                await apiService.updateCDR(matchedCall.id, updateData);
                console.log('✅ Call data saved to CDR successfully');
              }

              // Add note if any
              if (notesToSave.trim()) {
                await apiService.addNote(matchedCall.id, notesToSave);
                console.log(`📝 Note saved: "${notesToSave.substring(0, 50)}..."`);
              }
            } else {
              console.warn('⚠️ No matching CDR record found within time window');
              console.log('Available records:', recentCalls.data.map(r => ({
                id: r.id,
                time: r.call_date_utc,
                number: r.caller_number
              })));
            }
          } else {
            console.warn('⚠️ No CDR records found for this call');
          }
        } catch (error) {
          console.error('❌ Failed to save call data:', error);
        }
      }, 5000); // Wait 5 seconds for Asterisk to write CDR
    }

    // Reset hold tracking
    setTotalHoldDuration(0);
    setHoldCount(0);
    setHoldStartTime(null);
    setCurrentHoldDuration(0);
    setCallStartTime(null);

    clearInterval(callTimerRef.current);
    stopAllTones();

    if (sipSessionRef.current) {
      try {
        if (!sipSessionRef.current.isEnded()) {
          sipSessionRef.current.terminate({
            status_code: 200,
            reason_phrase: 'Call Ended by User'
          });
        }
      } catch (error) {
        console.error('Session termination error:', error);
      }
      sipSessionRef.current = null;
    }

    // Save call to history with duration before resetting
    if (callNumber && callDuration > 0) {
      const callType = activeCallCard?.direction || 'outgoing';
      saveCallToHistory({
        number: callNumber,
        type: callType,
        status: 'completed',
        duration: callDuration
      });
    }

    setInCall(false);
    setIncomingCall(null);
    setCallerInfo(null);
    setCallDuration(0);
    setShowTransfer(false);
    setOnHold(false);
    setCallStatus('');
    setCallNumber('');
    setMuted(false); // Reset mute state
    setSpeakerMuted(false); // Reset speaker mute state
    setActiveCallCard(null); // Close the call card
    setIncomingCallNotes('');
    setIncomingCallTags([]);

    if (remoteAudioRef.current) {
      if (remoteAudioRef.current.srcObject) {
        remoteAudioRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.pause();
    }
  };

  const handleAcceptCall = async () => {
    if (!incomingCall) return;
    
    // FIX #7: CRITICAL - Stop ringtone IMMEDIATELY before anything else
    stopAllTones();
    console.log('📞 Accepting call - ringtone stopped');
    
    // Initialize and resume audio context (Safari/Firefox fix)
    initAudioContext();
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('🔊 Audio context resumed on accept');
      } catch (error) {
        console.error('Failed to resume audio context:', error);
      }
    }

    const options = {
      mediaConstraints: { audio: true, video: false }
    };

    incomingCall.answer(options);

    saveCallToHistory({
      number: callerInfo?.number || callNumber,
      type: 'incoming',
      status: 'completed'
    });
  };

  const handleRejectCall = () => {
    // Stop tones immediately before any state updates
    stopAllTones();
    
    if (incomingCall) {
      incomingCall.terminate();
      setIncomingCall(null);
      setCallerInfo(null);

      saveCallToHistory({
        number: callerInfo?.number || callNumber,
        type: 'incoming',
        status: 'rejected'
      });
    }
  };

  const handleDialPad = (digit) => {
    setCallNumber(prev => prev + digit);
    if (inCall && sipSessionRef.current) {
      sipSessionRef.current.sendDTMF(digit);
    }
  };

  const toggleMute = () => {
    if (sipSessionRef.current && inCall) {
      try {
        if (muted) {
          sipSessionRef.current.unmute();
          setMuted(false);
          console.log('🎤 Microphone unmuted');
        } else {
          sipSessionRef.current.mute();
          setMuted(true);
          console.log('🔇 Microphone muted');
        }
      } catch (error) {
        console.error('Mute toggle error:', error);
        // Reset to actual state if toggle fails
        setMuted(!muted);
      }
    }
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !remoteAudioRef.current.muted;
      setSpeakerMuted(!speakerMuted);
    }
  };

  const toggleHold = () => {
    if (!sipSessionRef.current) {
      console.error('❌ No active SIP session for hold');
      return;
    }
    
    try {
      if (onHold) {
        // Resuming from hold - calculate duration
        console.log('🔄 Resuming call from hold...');
        
        const duration = holdStartTime ? Math.floor((Date.now() - holdStartTime) / 1000) : 0;
        
        // First, unhold the SIP session
        sipSessionRef.current.unhold();
        
        // Then update state in a single batch using setTimeout to avoid React DOM errors
        setTimeout(() => {
          if (holdStartTime) {
            setTotalHoldDuration(prev => prev + duration);
            console.log(`📞 Call resumed (was on hold for ${duration}s)`);
          }
          setHoldStartTime(null);
          setCurrentHoldDuration(0);
          setOnHold(false);
        }, 0);
        
      } else {
        // Putting on hold - record start time
        console.log('⏸️  Putting call on hold...');
        
        const now = Date.now();
        
        // First, hold the SIP session
        sipSessionRef.current.hold();
        
        // Then update state in a single batch using setTimeout to avoid React DOM errors
        setTimeout(() => {
          setHoldStartTime(now);
          setHoldCount(prev => prev + 1);
          setOnHold(true);
          console.log('✅ Call on hold');
        }, 0);
      }
    } catch (error) {
      console.error('❌ Hold toggle error:', error);
      
      // Revert state on error to prevent inconsistent UI
      setTimeout(() => {
        setOnHold(false);
        setHoldStartTime(null);
      }, 0);
      
      // Show user-friendly error
      alert('Failed to toggle hold. Please try again.');
    }
  };

  // FIX #6: Update current hold duration every second with proper cleanup
  useEffect(() => {
    let holdTimer;
    if (onHold && holdStartTime) {
      holdTimer = setInterval(() => {
        setCurrentHoldDuration(Math.floor((Date.now() - holdStartTime) / 1000));
      }, 1000);
      
      // FIX #6: Store reference for global cleanup
      holdTimerRef.current = holdTimer;
    }
    return () => {
      // FIX #6: Proper cleanup of hold timer
      if (holdTimer) {
        clearInterval(holdTimer);
      }
      if (holdTimerRef.current === holdTimer) {
        holdTimerRef.current = null;
      }
    };
  }, [onHold, holdStartTime]);

  const handleTransfer = async () => {
    if (!sipSessionRef.current || !transferTarget) return;

    try {
      if (transferType === 'queue') {
        await apiService.transferToQueue(transferTarget);
      } else {
        await apiService.transferToExtension(transferTarget);
      }

      alert('Call transferred successfully');
      handleEndCall();
      setShowTransfer(false);
    } catch (error) {
      alert('Transfer failed: ' + error.message);
    }
  };

  // Category Management
  const loadCallTypes = async () => {
    try {
      const types = await apiService.getCallTypes();
      setCallTypes(types);
    } catch (error) {
      console.error('Failed to load call types:', error);
    }
  };

  const loadLvl1Categories = async () => {
    try {
      const lvl1 = await apiService.getCategoriesLvl1();
      setCategories(prev => ({ ...prev, lvl1 }));
    } catch (error) {
      console.error('Failed to load level 1 categories:', error);
    }
  };

  const handleCategoryLvl1Change = async (lvl1Id) => {
    setSelectedCategories({
      lvl1: lvl1Id,
      lvl2: null,
      lvl3: null,
      callType: selectedCategories.callType
    });
    
    if (lvl1Id) {
      try {
        const lvl2 = await apiService.getCategoriesLvl2(lvl1Id);
        setCategories(prev => ({ ...prev, lvl2, lvl3: [] }));
      } catch (error) {
        console.error('Failed to load level 2 categories:', error);
      }
    } else {
      setCategories(prev => ({ ...prev, lvl2: [], lvl3: [] }));
    }
  };

  const handleCategoryLvl2Change = async (lvl2Id) => {
    setSelectedCategories(prev => ({
      ...prev,
      lvl2: lvl2Id,
      lvl3: null
    }));
    
    if (lvl2Id) {
      try {
        const lvl3 = await apiService.getCategoriesLvl3(lvl2Id);
        setCategories(prev => ({ ...prev, lvl3 }));
      } catch (error) {
        console.error('Failed to load level 3 categories:', error);
      }
    } else {
      setCategories(prev => ({ ...prev, lvl3: [] }));
    }
  };

  const handleCategoryLvl3Change = (lvl3Id) => {
    setSelectedCategories(prev => ({
      ...prev,
      lvl3: lvl3Id
    }));
  };

  const handleCallTypeChange = (callTypeId) => {
    setSelectedCategories(prev => ({
      ...prev,
      callType: callTypeId
    }));
  };

  const handleSaveCategories = async () => {
    if (!selectedCall) return;
    
    // Validation
    if (!selectedCategories.lvl1) {
      alert('Будь ласка, оберіть категорію рівня 1');
      return;
    }
    if (!selectedCategories.lvl2) {
      alert('Будь ласка, оберіть категорію рівня 2');
      return;
    }
    if (!selectedCategories.lvl3) {
      alert('Будь ласка, оберіть категорію рівня 3');
      return;
    }
    if (!selectedCategories.callType) {
      alert('Будь ласка, оберіть тип звернення');
      return;
    }
    
    try {
      await apiService.updateCDR(selectedCall.call.id, {
        category_lvl1_id: selectedCategories.lvl1,
        category_lvl2_id: selectedCategories.lvl2,
        category_lvl3_id: selectedCategories.lvl3,
        call_type_id: selectedCategories.callType
      });
      
      const detail = await apiService.getCDRDetail(selectedCall.call.id);
      setSelectedCall(detail);
      
      alert('Категорії збережено успішно!');
    } catch (error) {
      console.error('Failed to save categories:', error);
      alert('Помилка збереження категорій');
    }
  };

  // CDR Management
  const handleCallClick = async (callId) => {
    try {
      const detail = await apiService.getCDRDetail(callId);
      setSelectedCall(detail);
      setNewNoteText('');
      
      // Load existing categories if present
      setSelectedCategories({
        lvl1: detail.call.category_lvl1_id || null,
        lvl2: detail.call.category_lvl2_id || null,
        lvl3: detail.call.category_lvl3_id || null,
        callType: detail.call.call_type_id || null
      });
      
      // Load cascading categories
      if (detail.call.category_lvl1_id) {
        const lvl2 = await apiService.getCategoriesLvl2(detail.call.category_lvl1_id);
        setCategories(prev => ({ ...prev, lvl2 }));
        
        if (detail.call.category_lvl2_id) {
          const lvl3 = await apiService.getCategoriesLvl3(detail.call.category_lvl2_id);
          setCategories(prev => ({ ...prev, lvl3 }));
        }
      }
    } catch (error) {
      alert('Failed to load call details: ' + error.message);
    }
  };

  const handleAddNote = async (callId) => {
    if (!newNoteText.trim()) return;
    try {
      await apiService.addNote(callId, newNoteText);
      const detail = await apiService.getCDRDetail(callId);
      setSelectedCall(detail);
      setNewNoteText('');
    } catch (error) {
      alert('Failed to add note: ' + error.message);
    }
  };

  // CSV Import
  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);
    try {
      const preview = await apiService.importCSVPreview(file);
      setImportPreview(preview);

      const autoMapping = {};
      preview.columns.forEach(col => {
        const lowerCol = col.toLowerCase();
        if (lowerCol.includes('uuid') || lowerCol.includes('id')) autoMapping.call_uuid = col;
        if (lowerCol.includes('date') || lowerCol.includes('time')) autoMapping.call_date = col;
        if (lowerCol.includes('direction')) autoMapping.direction = col;
        if (lowerCol.includes('caller') || lowerCol.includes('from')) autoMapping.caller_number = col;
        if (lowerCol.includes('called') || lowerCol.includes('to')) autoMapping.called_number = col;
        if (lowerCol.includes('duration') || lowerCol.includes('talk')) autoMapping.talk_duration = col;
        if (lowerCol.includes('status')) autoMapping.call_status = col;
        if (lowerCol.includes('queue')) autoMapping.queue_name = col;
        if (lowerCol.includes('agent')) autoMapping.agent_sip = col;
      });

      setImportMapping(autoMapping);
    } catch (error) {
      alert('Failed to preview CSV: ' + error.message);
    }
  };

  const handleCSVImport = async () => {
    if (!importFile || !importMapping.call_date || !importMapping.direction) {
      alert('Please map required fields: Call Date and Direction');
      return;
    }

    try {
      setImportStatus({ status: 'importing', message: 'Importing...' });
      const result = await apiService.importCSVExecute(importFile, importMapping);
      setImportStatus({
        status: 'success',
        message: `✅ Imported ${result.imported} calls, ${result.failed} failed`
      });
      loadCDR();
    } catch (error) {
      setImportStatus({ status: 'error', message: '❌ Import failed: ' + error.message });
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Direction', 'From', 'To', 'Queue', 'Agent', 'Duration', 'Hold', 'Hold Count', 'Status', 'Tags'];
    const rows = cdrData.data.map(record => [
      record.call_date_local,
      record.direction,
      record.caller_number,
      record.called_number,
      record.queue_name || '',
      record.agent_sip || '',
      record.talk_duration_formatted,
      record.hold_duration > 0 ? record.hold_duration_formatted : '-',
      record.hold_count || '0',
      record.call_status,
      record.tags?.join('; ') || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calls_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Utility Functions
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format seconds to HH:MM format
  const formatTimeHHMM = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  // FIX #5: Resume audio context on any user interaction (critical for Safari/Firefox)
  useEffect(() => {
    const resumeAudioContext = async () => {
      if (audioContextRef.current?.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log('🔊 Audio context resumed on user interaction');
          // Remove listener after first successful resume
          document.removeEventListener('click', resumeAudioContext);
          document.removeEventListener('touchstart', resumeAudioContext);
        } catch (error) {
          console.error('Failed to resume audio context:', error);
        }
      }
    };
    
    // Listen for any user interaction
    document.addEventListener('click', resumeAudioContext);
    document.addEventListener('touchstart', resumeAudioContext);
    
    return () => {
      document.removeEventListener('click', resumeAudioContext);
      document.removeEventListener('touchstart', resumeAudioContext);
    };
  }, []);

  // Effects
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (isLoggedIn) {
      const interval = setInterval(() => {
        loadQueues();
        loadMyQueueMemberships();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, loadQueues, loadMyQueueMemberships]);

  // Check microphone status when user logs in
  useEffect(() => {
    if (isLoggedIn && sipConnected) {
      checkMicrophoneStatus();
      
      // Recheck every 30 seconds
      const micCheckInterval = setInterval(() => {
        checkMicrophoneStatus();
      }, 30000);
      
      return () => clearInterval(micCheckInterval);
    }
  }, [isLoggedIn, sipConnected]);

  // Sync mute state with SIP session once when call starts
  useEffect(() => {
    if (inCall && sipSessionRef.current) {
      // Only sync once when call starts, not continuously
      try {
        if (typeof sipSessionRef.current.isMuted === 'function') {
          const actualMuted = sipSessionRef.current.isMuted();
          if (actualMuted !== muted) {
            console.log('🔄 Syncing initial mute state. Actual:', actualMuted);
            setMuted(actualMuted);
          }
        }
      } catch (error) {
        console.error('Mute state sync error:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCall]); // Only run when inCall changes

  useEffect(() => {
    const session = loadSession();
    if (session && apiService.isAuthenticated()) {
      setUser(session.userData);
      setIsLoggedIn(true);

      if (session.permissions) {
        setPermissions(session.permissions);
      }

      connectSIP(session.userData.sipNumber, session.userData.sipPassword);

      loadQueues();
      loadAllQueues(); // Load all queues for transfer dropdown
      loadCDR();
      loadTags();
      loadCallHistory();
      loadMyQueueMemberships();
      loadAgentStats();

      if (session.permissions?.can_manage_users || session.permissions?.is_admin) {
        loadUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Removed drag effect - phone is now a static bottom bar

  useEffect(() => {
    if (isLoggedIn) loadCDR();
  }, [filters, isLoggedIn, loadCDR]);

  // Load categories and call types when user logs in
  useEffect(() => {
    if (user) {
      loadCallTypes();
      loadLvl1Categories();
    }
  }, [user]);

  // Auto-refresh CDR every 10 seconds if enabled
  useEffect(() => {
    let refreshInterval;
    if (autoRefreshCDR && isLoggedIn && currentView === 'calls') {
      refreshInterval = setInterval(() => {
        console.log('🔄 Auto-refreshing Call History...');
        loadCDR();
      }, 10000); // 10 seconds
    }
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [autoRefreshCDR, isLoggedIn, currentView, loadCDR]);

  useEffect(() => {
    if (currentView === 'analytics' && isLoggedIn) {
      loadOverallStats();
    }
  }, [currentView, isLoggedIn, loadOverallStats]);

  // FIX #6: Comprehensive timer cleanup to prevent memory leaks
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up all timers and resources...');
      
      // Clean up call timer
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      
      // Clean up hold timer
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      
      // Clean up tone timeout
      if (toneTimeoutRef.current) {
        clearInterval(toneTimeoutRef.current);
        toneTimeoutRef.current = null;
      }
      
      // Clean up any active audio
      if (ringtoneRef.current) {
        try {
          ringtoneRef.current.pause();
          ringtoneRef.current.src = '';
        } catch (e) {
          console.error('Error cleaning up ringtone:', e);
        }
        ringtoneRef.current = null;
      }
      
      if (remoteAudioRef.current) {
        try {
          remoteAudioRef.current.pause();
          remoteAudioRef.current.srcObject = null;
        } catch (e) {
          console.error('Error cleaning up remote audio:', e);
        }
        remoteAudioRef.current = null;
      }
      
      // Clean up SIP UA
      if (sipUARef.current) {
        sipUARef.current.stop();
      }
    };
  }, []);

  // Advanced Connection Quality Monitoring - check every 10 seconds during calls
  useEffect(() => {
    let qualityInterval;
    let predictiveInterval;
    
    if (inCall) {
      // Initial check
      checkConnectionQuality();
      
      // Check every 10 seconds during call
      qualityInterval = setInterval(checkConnectionQuality, 10000);
      
      // Predictive analysis every 30 seconds
      predictiveInterval = setInterval(predictiveCleanup, 30000);
    }
    
    return () => {
      if (qualityInterval) clearInterval(qualityInterval);
      if (predictiveInterval) clearInterval(predictiveInterval);
    };
  }, [inCall, checkConnectionQuality, predictiveCleanup]);

  // Stale Call State Cleanup - check every minute
  useEffect(() => {
    const staleStateCleanup = setInterval(() => {
      if (inCall && callStartTime) {
        const callDuration = Date.now() - callStartTime.getTime();
        
        // If call has been "active" for more than 4 hours, probably stale
        if (callDuration > 4 * 60 * 60 * 1000) {
          console.warn('🚨 Cleaning up stale call state (4+ hours)');
          forceCleanupCallState();
        }
        
        // Also check if SIP session is actually ended but state not updated
        if (sipSessionRef.current && sipSessionRef.current.isEnded()) {
          console.warn('🚨 SIP session ended but state not updated - cleaning up');
          forceCleanupCallState();
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(staleStateCleanup);
  }, [inCall, callStartTime, forceCleanupCallState]);

  // Performance Monitoring - start monitoring when logged in
  useEffect(() => {
    let performanceInterval;
    let memoryInterval;
    
    if (isLoggedIn) {
      // Start performance monitoring
      performanceMonitor.startMonitoring();
      
      // Generate performance report every 5 minutes
      performanceInterval = setInterval(() => {
        const report = generateReport();
        console.log('📊 Performance Report:', report);
      }, 5 * 60 * 1000);
      
      // Check memory usage every 30 seconds
      memoryInterval = setInterval(() => {
        const memoryInfo = performanceMonitor.monitorMemory();
        if (memoryInfo) {
          recordMetric('memory_usage_percent', (memoryInfo.used / memoryInfo.limit) * 100);
        }
      }, 30000);
    }
    
    return () => {
      if (performanceInterval) clearInterval(performanceInterval);
      if (memoryInterval) clearInterval(memoryInterval);
      if (isLoggedIn) {
        performanceMonitor.stopMonitoring();
      }
    };
  }, [isLoggedIn]);

  // Enhanced SIP Session Error Handling
  useEffect(() => {
    if (sipSessionRef.current && inCall) {
      const session = sipSessionRef.current;
      
      // Add additional error handlers
      const handleSessionFailed = (e) => {
        console.log('🚨 SIP session failed:', e);
        setTimeout(() => {
          if (inCall) {
            console.log('🧹 Auto-cleanup after session failure');
            forceCleanupCallState();
          }
        }, 1000);
      };
      
      const handleSessionEnded = (e) => {
        console.log('📞 SIP session ended:', e);
        // Normal cleanup will happen via handleEndCall
      };
      
      // Add listeners
      session.on('failed', handleSessionFailed);
      session.on('ended', handleSessionEnded);
      
      return () => {
        // Cleanup listeners
        if (session && !session.isEnded()) {
          session.removeListener('failed', handleSessionFailed);
          session.removeListener('ended', handleSessionEnded);
        }
      };
    }
  }, [inCall, forceCleanupCallState]);

  // Handle page refresh/close - terminate active calls
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (inCall && sipSessionRef.current) {
        // Terminate the active call
        try {
          if (!sipSessionRef.current.isEnded()) {
            sipSessionRef.current.terminate({
              status_code: 200,
              reason_phrase: 'Page Refresh'
            });
          }
        } catch (error) {
          console.error('Error terminating call on page unload:', error);
        }
        
        // Show warning to user
        e.preventDefault();
        e.returnValue = language === 'uk' 
          ? 'У вас активний дзвінок. Ви впевнені, що хочете оновити сторінку?' 
          : 'You have an active call. Are you sure you want to refresh?';
        return e.returnValue;
      }
    };

    // Note: Removed visibilitychange handler as it was ending calls when switching windows
    // The beforeunload handler is sufficient for page refresh/close scenarios
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [inCall, language]);
  // Theme colors
  const theme = {
    bg: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    cardBorder: darkMode ? 'border-gray-700' : 'border-gray-200',
    text: darkMode ? 'text-gray-100' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-400' : 'text-gray-600',
    input: darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900',
    inputFocus: darkMode ? 'focus:border-blue-500' : 'focus:border-blue-500',
    hover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    tableBg: darkMode ? 'bg-gray-800' : 'bg-white',
    tableHeader: darkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRow: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    tableBorder: darkMode ? 'border-gray-700' : 'border-gray-200',
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900'} flex items-center justify-center p-4`}>
        <div className={`${theme.card} rounded-2xl shadow-2xl p-8 w-full max-w-md`}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Phone className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-3xl font-bold ${theme.text}`}>{t('appTitle')}</h1>
            <p className={`${theme.textSecondary} mt-2`}>{t('signInLDAP')}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className={`w-full px-4 py-3 border ${theme.input} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder={t('ldapUsername')}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className={`w-full px-4 py-3 border ${theme.input} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder={t('passwordPlaceholder')}
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Sign In
            </button>
          </form>

          {/* Browser Permissions Info */}
          <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-yellow-900 bg-opacity-30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${darkMode ? 'text-yellow-400' : 'text-yellow-800'} mb-2`}>
                  {language === 'uk' ? 'Необхідні дозволи браузера' : 'Browser Permissions Required'}
                </h4>
                <ul className={`text-xs ${darkMode ? 'text-yellow-300' : 'text-yellow-700'} space-y-1`}>
                  <li className="flex items-center space-x-2">
                    <Mic className="w-3 h-3" />
                    <span>
                      {language === 'uk' 
                        ? 'Мікрофон - для здійснення дзвінків' 
                        : 'Microphone - for making calls'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Volume2 className="w-3 h-3" />
                    <span>
                      {language === 'uk' 
                        ? 'Звук - для відтворення дзвінків' 
                        : 'Audio - for playing calls'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span>
                      {language === 'uk' 
                        ? 'Сповіщення - для вхідних дзвінків' 
                        : 'Notifications - for incoming calls'}
                    </span>
                  </li>
                </ul>
                <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'} mt-2`}>
                  {language === 'uk' 
                    ? 'Після входу браузер запитає ці дозволи. Будь ласка, дозвольте їх для коректної роботи.' 
                    : 'After login, the browser will request these permissions. Please allow them for proper functionality.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Application
  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('importCDRFromCSV')}</h2>
              <button onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview(null);
                setImportStatus(null);
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {!importPreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('uploadCSVPreview')}</p>
                <label className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                  <Upload className="w-5 h-5 mr-2" />
                  Select CSV File
                  <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
                </label>
              </div>
            ) : importStatus?.status === 'success' ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{importStatus.message}</h3>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportPreview(null);
                    setImportStatus(null);
                  }}
                  className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">{t('columnMapping')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['call_date', 'direction', 'caller_number', 'called_number', 'queue_name', 'agent_sip', 'talk_duration', 'call_status'].map(field => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.replace(/_/g, ' ').toUpperCase()}
                          {['call_date', 'direction'].includes(field) && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={importMapping[field] || ''}
                          onChange={(e) => setImportMapping({...importMapping, [field]: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select column...</option>
                          {importPreview.columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="overflow-x-auto mb-6 max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {importPreview.columns.map(col => (
                          <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview.slice(0, 10).map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          {importPreview.columns.map(col => (
                            <td key={col} className="px-3 py-2 text-xs text-gray-600 border">
                              {row[col]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {importStatus?.status === 'error' && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {importStatus.message}
                  </div>
                )}

                {importStatus?.status === 'importing' && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
                    {importStatus.message}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      setImportFile(null);
                      setImportPreview(null);
                      setImportStatus(null);
                    }}
                    className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCSVImport}
                    disabled={!importMapping.call_date || !importMapping.direction || importStatus?.status === 'importing'}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Import {importPreview.totalLines} Rows
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

{/* Call Details Modal */}
{selectedCall && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t('callDetails')}</h2>
        <button onClick={() => setSelectedCall(null)} className="text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Date & Time</label>
            <p className="text-gray-900 font-mono">{selectedCall.call.call_date_local}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Direction</label>
            <p className="text-gray-900">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                selectedCall.call.direction === 'INBOUND' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {selectedCall.call.direction}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">From</label>
            <p className="text-gray-900 font-mono">{selectedCall.call.caller_number}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">To</label>
            <p className="text-gray-900 font-mono">{selectedCall.call.called_number}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Queue</label>
            <p className="text-gray-900">{selectedCall.call.queue_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Agent</label>
            <p className="text-gray-900">{selectedCall.call.agent_sip || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Duration</label>
            <p className="text-gray-900 font-mono">{selectedCall.call.talk_duration_formatted}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Status</label>
            <p className="text-gray-900">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                selectedCall.call.call_status === 'ANSWERED' ? 'bg-green-100 text-green-800' : 
                selectedCall.call.call_status === 'NO ANSWER' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'
              }`}>
                {selectedCall.call.call_status === 'NO ANSWER' && selectedCall.call.agent_sip === 'MISSED' ? t('missed') : 
                 selectedCall.call.call_status === 'NO ANSWER' && selectedCall.call.agent_sip === 'ABANDONED' ? (language === 'uk' ? 'Покинуто' : 'Abandoned') :
                 selectedCall.call.call_status === 'NO ANSWER' && selectedCall.call.agent_sip === 'NO_AGENTS' ? (language === 'uk' ? 'Немає агентів' : 'No Agents') :
                 selectedCall.call.call_status}
              </span>
            </p>
          </div>
        </div>

        {/* Add to Contacts Button */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              // Determine which number to add based on call direction
              const phoneNumber = selectedCall.call.direction === 'INBOUND' 
                ? selectedCall.call.caller_number 
                : selectedCall.call.called_number;
              
              // Get caller name from various possible fields
              const callerName = selectedCall.call.caller_id_name || 
                                selectedCall.call.caller_name || 
                                selectedCall.call.cnam || 
                                '';
              
              // Switch to contacts view and pre-fill form
              setCurrentView('contacts');
              setTimeout(() => {
                // Trigger add contact with pre-filled data
                window.dispatchEvent(new CustomEvent('addContactFromCall', {
                  detail: {
                    phone_number: phoneNumber,
                    full_name: callerName,
                    notes: `Added from ${selectedCall.call.direction.toLowerCase()} call on ${selectedCall.call.call_date_local}`
                  }
                }));
              }, 100);
              setSelectedCall(null);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
          >
            <User className="w-4 h-4" />
            {language === 'uk' ? 'Додати до контактів' : 'Add to Contacts'}
          </button>
        </div>

        {/* Tags section - Hidden but kept for historical data */}
        {/* <div>
          <label className="text-sm font-medium text-gray-600 mb-2 block">Tags</label>
          ...
        </div> */}

        {/* Category Section */}
        <div className="mb-6 border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Категоризація звернення</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Level 1 */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Категорія рівня 1 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategories.lvl1 || ''}
                onChange={(e) => handleCategoryLvl1Change(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Оберіть значення</option>
                {categories.lvl1.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Level 2 */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Категорія рівня 2 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategories.lvl2 || ''}
                onChange={(e) => handleCategoryLvl2Change(e.target.value || null)}
                disabled={!selectedCategories.lvl1}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Оберіть значення</option>
                {categories.lvl2.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Level 3 */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Категорія рівня 3 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategories.lvl3 || ''}
                onChange={(e) => handleCategoryLvl3Change(e.target.value || null)}
                disabled={!selectedCategories.lvl2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Оберіть значення</option>
                {categories.lvl3.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Call Type */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Тип звернення <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategories.callType || ''}
                onChange={(e) => handleCallTypeChange(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Оберіть значення</option>
                {callTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-4">
            <button
              onClick={handleSaveCategories}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Зберегти категорії
            </button>
          </div>

          {/* Display saved categories */}
          {selectedCall.call.category_lvl1_text && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Збережені категорії:</p>
              <p className="text-sm text-gray-900"><strong>Lvl1:</strong> {selectedCall.call.category_lvl1_text}</p>
              <p className="text-sm text-gray-900"><strong>Lvl2:</strong> {selectedCall.call.category_lvl2_text}</p>
              <p className="text-sm text-gray-900"><strong>Lvl3:</strong> {selectedCall.call.category_lvl3_text}</p>
              <p className="text-sm text-gray-900"><strong>Тип:</strong> {selectedCall.call.call_type_text}</p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-600">Notes</label>
            {selectedCall.notes && selectedCall.notes.length > 0 && (
              <button
                onClick={() => {
                  // Format notes for Jira
                  const jiraFormat = `*Call Details*\n` +
                    `Date: ${selectedCall.call.call_date_local}\n` +
                    `From: ${selectedCall.call.caller_number}\n` +
                    `To: ${selectedCall.call.called_number}\n` +
                    `Duration: ${selectedCall.call.talk_duration_formatted}\n` +
                    (selectedCall.call.hold_duration > 0 ? `Hold: ${selectedCall.call.hold_duration_formatted} (${selectedCall.call.hold_count}x)\n` : '') +
                    `Status: ${selectedCall.call.call_status}\n\n` +
                    `*Notes:*\n` +
                    selectedCall.notes.map(note => 
                      `- [${new Date(note.created_at).toLocaleString('uk-UA', { timeZone: 'Europe/Kiev' })}] ${note.author}: ${note.note_text}`
                    ).join('\n');
                  
                  // Copy to clipboard
                  navigator.clipboard.writeText(jiraFormat).then(() => {
                    alert(t('copiedToClipboard'));
                  }).catch(err => {
                    console.error('Failed to copy:', err);
                    alert(t('failedToCopy'));
                  });
                }}
                className="flex items-center space-x-1 px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                title={t('copyForJira')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>{t('copyForJira')}</span>
              </button>
            )}
          </div>
          <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
            {selectedCall.notes?.map((note) => (
              <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium text-gray-900">{note.author}</span>
                  <span className="text-xs text-gray-500">
                    {new Date(note.created_at).toLocaleString('uk-UA', { timeZone: 'Europe/Kiev' })}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{note.note_text}</p>
              </div>
            ))}
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder={t('addNote')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddNote(selectedCall.call.id);
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => handleAddNote(selectedCall.call.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {selectedCall.call.recording_available && (
          <div>
            <label className="text-sm font-medium text-gray-600 mb-2 block">Recording</label>
            <div className="bg-gray-50 rounded-lg p-4">
              <audio controls className="w-full" preload="metadata">
                <source
                  src={apiService.getRecordingStreamUrl(selectedCall.call.recording_path.split('/').pop())}
                  type="audio/wav"
                />
                Your browser does not support the audio element.
              </audio>
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>📁 {selectedCall.call.recording_path}</span>
                <a
                  href={apiService.getRecordingStreamUrl(selectedCall.call.recording_path.split('/').pop())}
                  download
                  className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                >
                  <Download className="w-3 h-3" />
                  <span>Download</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
)}

      {/* Queue Manager Modal */}
      {showQueueManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('queueManagementTitle')}</h2>
              <button onClick={() => setShowQueueManager(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('myActiveQueues')}</h3>
              {myQueueMemberships.length > 0 ? (
                <div className="space-y-2">
                  {myQueueMemberships.map((membership) => (
                    <div key={membership.queue_name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-semibold text-gray-900">{membership.display_name || membership.queue_name}</div>
                        <div className="text-sm text-gray-600">
                          Logged in: {new Date(membership.login_time).toLocaleString('uk-UA', { timeZone: 'Europe/Kiev' })}
                        </div>
                        {membership.paused && <div className="text-sm text-yellow-600 font-medium">⏸️ Paused</div>}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleQueuePause(membership.queue_name, !membership.paused)}
                          className={`px-3 py-1 text-sm rounded-lg ${
                            membership.paused
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          }`}
                        >
                          {membership.paused ? 'Unpause' : 'Pause'}
                        </button>
                        <button
                          onClick={() => handleQueueLogout(membership.queue_name)}
                          className="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Not logged into any queues</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('availableQueues')}</h3>
              <div className="space-y-2">
                {user?.allowedQueues
                  ?.filter(queueName => !myQueueMemberships.some(m => m.queue_name === queueName))
                  .map((queueName) => (
                    <div key={queueName} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-semibold text-gray-900">{queues[queueName]?.name || queueName}</div>
                        <div className="text-sm text-gray-600">{queues[queueName]?.available || 0} agents available</div>
                      </div>
                      <button
                        onClick={() => handleQueueLogin(queueName)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                      >
                        Login
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`${theme.card} shadow-sm border-b ${theme.cardBorder} sticky top-0 z-40`}>
        <div className="max-w-full px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className={`text-lg font-bold ${theme.text}`}>Comfy Call Center</h1>
                <div className="flex items-center space-x-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${sipConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={theme.textSecondary}>{sipConnected ? 'Online' : 'Offline'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            <div className={`flex items-center space-x-2 text-sm ${theme.text} ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} px-4 py-2 rounded-lg`}>
              <User className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{user?.ldapUser}</span>
              <span className={theme.textSecondary}>|</span>
              <span className={theme.textSecondary}>SIP: {user?.sipNumber}</span>
            </div>

            <button
              onClick={() => setShowQueueManager(true)}
              className={`flex items-center space-x-2 px-4 py-2 text-blue-600 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'} rounded-lg transition-colors`}
            >
              <Users className="w-4 h-4" />
              <span className="font-medium">Queues</span>
              {myQueueMemberships.length > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {myQueueMemberships.length}
                </span>
              )}
            </button>

            {/* Missed Call Notifications */}
            <MissedCallNotifications 
              token={localStorage.getItem('authToken')}
              agentSip={user?.sipNumber}
              darkMode={darkMode}
              onCallBack={(number) => {
                setCallNumber(number);
                // Small delay to ensure state is updated
                setTimeout(() => {
                  if (sipConnected && !inCall) {
                    handleCall();
                  }
                }, 100);
              }}
            />

            <button
              onClick={handleLogout}
              className={`flex items-center space-x-2 px-4 py-2 text-red-600 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'} rounded-lg transition-colors`}
            >
              <LogOut className="w-4 h-4" />
              <span className="font-medium">{t('logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-full px-6 py-6 pb-24">
        {/* Microphone Warning */}
        {showMicWarning && !microphoneStatus.available && (
          <div className={`mb-4 p-4 rounded-lg border-2 ${darkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200'} flex items-start gap-3`}>
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className={`font-semibold ${darkMode ? 'text-red-200' : 'text-red-800'} mb-1`}>
                🎤 Microphone Not Available
              </h3>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'} mb-2`}>
                {microphoneStatus.error || 'Cannot access microphone. You will not be able to make or receive calls.'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={checkMicrophoneStatus}
                  className={`text-sm px-3 py-1 rounded ${darkMode ? 'bg-red-800 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                >
                  Retry
                </button>
                <button
                  onClick={() => setShowMicWarning(false)}
                  className={`text-sm px-3 py-1 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                >
                  Dismiss
                </button>
                <a
                  href="https://support.google.com/chrome/answer/2693767"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm px-3 py-1 rounded ${darkMode ? 'bg-blue-800 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                >
                  Help
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setCurrentView('queues')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'queues'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                {t('queues')}
              </button>
              <button
                onClick={() => setCurrentView('calls')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'calls'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                {t('callHistory')}
              </button>
              <button
                onClick={() => setCurrentView('contacts')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'contacts'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Contacts
              </button>
              <button
                onClick={() => {
                  setCurrentView('analytics');
                  loadOverallStats();
                }}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'analytics'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                {t('analytics')}
              </button>
              <button
                onClick={() => {
                  setCurrentView('agent-stats');
                  loadAgentStats();
                }}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'agent-stats'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                {t('myStatistics')}
              </button>
{/* Show KPI Report only if user has permission */}
{(permissions.can_view_kpi || permissions.is_admin) && (
  <button
    onClick={() => {
      setCurrentView('kpi-report');
      loadKPIReport();
    }}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'kpi-report'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <BarChart3 className="w-4 h-4 inline mr-2" />
    {t('kpiReport')}
  </button>
)}
{/* Show KPI 1st Line Report only if user has permission */}
{(permissions.can_view_kpi || permissions.is_admin) && (
  <button
    onClick={() => {
      setCurrentView('kpi-1st-line');
      loadKPIReport();
    }}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'kpi-1st-line'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <BarChart3 className="w-4 h-4 inline mr-2" />
    {language === 'uk' ? 'KPI 1 Лінія' : 'KPI 1st Line'}
  </button>
)}
{/* Show Tags Management only if user has permission */}
{(permissions.can_edit_tags || permissions.is_admin) && (
  <button
    onClick={() => setCurrentView('tags')}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'tags'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Tag className="w-4 h-4 inline mr-2" />
    {t('tagsManagement')}
  </button>
)}
{/* Show User Management only if user has permission */}
{(permissions.can_manage_users || permissions.is_admin) && (
  <button
    onClick={() => {
      setCurrentView('user-management');
      loadUsers();
    }}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'user-management'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Users className="w-4 h-4 inline mr-2" />
    {t('userManagement')}
  </button>
)}
{/* Show Queue Management only if user has permission */}
{(permissions.can_manage_queues || permissions.is_admin) && (
  <button
    onClick={() => setCurrentView('queue-management')}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'queue-management'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Settings className="w-4 h-4 inline mr-2" />
    {t('queueManagement')}
  </button>
)}
{/* Show Category Management only if user is admin */}
{permissions.is_admin && (
  <button
    onClick={() => setCurrentView('category-management')}
    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      currentView === 'category-management'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <FileText className="w-4 h-4 inline mr-2" />
    {language === 'uk' ? 'Управління довідниками' : 'Category Management'}
  </button>
)}
                <button
                  onClick={() => {
                    setCurrentView('all-queues');
                    loadQueues();
                  }}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    currentView === 'all-queues'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  {t('allQueues')}
                </button>
                <button
                  onClick={() => setCurrentView('settings')}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    currentView === 'settings'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Volume2 className="w-4 h-4 inline mr-2" />
                  {t('settings')}
                </button>
            </nav>
          </div>
        </div>
        {/* Queue Dashboard View */}
        {currentView === 'queues' && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold ${theme.text}`}>{t('queues')}</h2>
              <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Моніторинг черг дзвінків у реальному часі' : 'Real-time monitoring of call queues'}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
              {Object.entries(queues).map(([queueId, queue]) => {
                // Check if this is a view-only queue
                const isViewOnly = user?.viewableQueues?.includes(queue.name) && !user?.allowedQueues?.includes(queue.name);
                
                return (
                <div key={queueId} className={`bg-white rounded-xl shadow-sm border ${isViewOnly ? 'border-yellow-300' : 'border-gray-200'} overflow-hidden hover:shadow-md transition-shadow`}>
                  <div className={`bg-gradient-to-r ${isViewOnly ? 'from-yellow-500 to-yellow-600' : 'from-blue-600 to-blue-700'} p-5`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-white mb-1">{queue.name}</h3>
                        <p className={`${isViewOnly ? 'text-yellow-100' : 'text-blue-100'} text-sm font-mono`}>{queueId}</p>
                      </div>
                      {isViewOnly && (
                        <span className="px-2 py-1 bg-white bg-opacity-20 text-white text-xs font-semibold rounded">
                          {language === 'uk' ? 'Тільки перегляд' : 'View Only'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="grid grid-cols-3 gap-4 mb-5">
                      <div className="text-center">
                        <div className={`text-3xl font-bold mb-1 ${queue.waiting > 2 ? 'text-red-600' : 'text-green-600'}`}>
                          {queue.waiting}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">WAITING</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-900 mb-1">{queue.agents}</div>
                        <div className="text-xs text-gray-600 font-medium">AGENTS</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-1">{queue.available}</div>
                        <div className="text-xs text-gray-600 font-medium">AVAILABLE</div>
                      </div>
                    </div>

                    {queue.members && queue.members.length > 0 && (
                      <div className="mb-5 border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Agents</h4>
                        <div className="space-y-2">
                          {queue.members.map((member, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  member.statusColor === 'green' ? 'bg-green-500' :
                                  member.statusColor === 'red' ? 'bg-red-500' :
                                  member.statusColor === 'yellow' ? 'bg-yellow-500' :
                                  'bg-gray-400'
                                }`}></div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{member.displayName || `${member.sipNumber}`}</div>
                                  <div className="text-xs text-gray-500">
                                    {member.status === 'In Call' && member.connectedNumber 
                                      ? `In call with ${member.connectedNumber}` 
                                      : member.status} • {member.callsTaken} calls
                                  </div>
                                </div>
                              </div>
                              {permissions.can_manage_queues && (
                                <button
                                  onClick={() => handleRemoveAgentFromQueue(queue.name, member.sipNumber, member.displayName)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Remove agent from queue"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {queue.waitingCallers && queue.waitingCallers.length > 0 && (
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">{t('waitingCallers')}</h4>
                          <span className="text-xs text-gray-500 bg-orange-50 text-orange-700 px-2 py-1 rounded-full font-medium">{t('live')}</span>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {queue.waitingCallers.map((caller, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full font-bold text-xs">
                                  {caller.position}
                                </div>
                                <div>
                                  <div className="font-mono text-sm font-semibold text-gray-900">{caller.phone}</div>
                                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                                    <Clock className="w-3 h-3" />
                                    <span>{caller.waitTime}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="animate-pulse">
                                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        )}

        {/* Call History View */}
        {currentView === 'calls' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={`text-2xl font-bold ${theme.text}`}>{t('callHistory')}</h2>
                <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Перегляд та управління записами дзвінків' : 'View and manage call records'}</p>
              </div>
              <div className="flex items-center space-x-3">
                {/* Auto-refresh toggle */}
                <label className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={autoRefreshCDR}
                    onChange={(e) => {
                      setAutoRefreshCDR(e.target.checked);
                      localStorage.setItem('autoRefreshCDR', JSON.stringify(e.target.checked));
                    }}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    {language === 'uk' ? 'Авто-оновлення (10с)' : 'Auto-refresh (10s)'}
                  </span>
                </label>

                {/* Manual refresh button */}
                <button
                  onClick={() => {
                    loadCDR();
                    console.log('🔄 Manual refresh triggered');
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  title={language === 'uk' ? 'Оновити' : 'Refresh'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{language === 'uk' ? 'Оновити' : 'Refresh'}</span>
                </button>

                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>{language === 'uk' ? 'Імпорт CSV' : 'Import CSV'}</span>
                </button>
                <button
                  onClick={exportToCSV}
                  disabled={cdrData.data.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  <span>{language === 'uk' ? 'Експорт CSV' : 'Export CSV'}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value, offset: 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value, offset: 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
                <select
                  value={filters.direction}
                  onChange={(e) => setFilters({...filters, direction: e.target.value, offset: 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="INBOUND">Inbound</option>
                  <option value="OUTBOUND">Outbound</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Queue</label>
                <select
                  value={filters.queue}
                  onChange={(e) => setFilters({...filters, queue: e.target.value, offset: 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Queues</option>
                  {user?.allowedQueues?.map(queueName => (
                    <option key={queueName} value={queueName}>{queueName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent</label>
                <input
                  type="text"
                  value={filters.agent}
                  onChange={(e) => setFilters({...filters, agent: e.target.value, offset: 0})}
                  placeholder={t('searchAgent')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className={`mb-4 text-sm ${theme.textSecondary}`}>
              Showing {cdrData.data.length} of {cdrData.total} calls
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${theme.cardBorder}`}>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Date & Time</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Direction</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>From</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>To</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Queue</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Agent</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Duration</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Hold</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Status</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Tags</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Audio</th>
                  </tr>
                </thead>
                <tbody>
                  {cdrData.data.map((record) => (
                    <tr
                      key={record.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleCallClick(record.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-800">{record.call_date_local}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          record.direction === 'INBOUND' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {record.direction === 'INBOUND' ? '📞 In' : '📱 Out'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">{record.caller_display || record.caller_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{record.called_display || record.called_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.queue_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.agent_display_name || record.agent_sip || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{record.talk_duration_formatted}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {record.hold_duration > 0 ? (
                          <span className="text-orange-600" title={`On hold ${record.hold_count} time(s)`}>
                            ⏸️ {record.hold_duration_formatted}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          record.call_status === 'ANSWERED' ? 'bg-green-100 text-green-800' : 
                          record.call_status === 'NO ANSWER' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {record.call_status === 'NO ANSWER' && record.agent_sip === 'MISSED' ? t('missed') : 
                           record.call_status === 'NO ANSWER' && record.agent_sip === 'ABANDONED' ? (language === 'uk' ? 'Покинуто' : 'Abandoned') :
                           record.call_status === 'NO ANSWER' && record.agent_sip === 'NO_AGENTS' ? (language === 'uk' ? 'Немає агентів' : 'No Agents') :
                           record.call_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {record.tags?.slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="inline-flex px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                          {record.tags?.length > 2 && (
                            <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              +{record.tags.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {record.recording_available ? (
                          <span className="text-green-600">🎵</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {cdrData.data.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No records found matching the filters
                </div>
              )}
            </div>

            {cdrData.total > filters.limit && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setFilters({...filters, offset: Math.max(0, filters.offset - filters.limit)})}
                  disabled={filters.offset === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <span className="text-sm text-gray-600">
                  Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.ceil(cdrData.total / filters.limit)}
                </span>

                <button
                  onClick={() => setFilters({...filters, offset: filters.offset + filters.limit})}
                  disabled={filters.offset + filters.limit >= cdrData.total}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Analytics View */}
        {currentView === 'analytics' && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold ${theme.text}`}>{t('analytics')}</h2>
              <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Загальні показники ефективності кол-центру' : 'Overall call center performance metrics'}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Queue</label>
                <select
                  value={filters.queue}
                  onChange={(e) => setFilters({...filters, queue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Queues</option>
                  {Object.entries(queues).map(([qid, q]) => (
                    <option key={qid} value={qid}>{q.name}</option>
                  ))}
                </select>
              </div>
            </div>

        {overallStats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-3xl font-bold text-blue-600 mb-2">{overallStats.total_calls || 0}</div>
              <div className="text-sm text-gray-600 font-medium">Total Calls</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-3xl font-bold text-green-600 mb-2">{overallStats.inbound_calls || 0}</div>
              <div className="text-sm text-gray-600 font-medium">Inbound</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-3xl font-bold text-purple-600 mb-2">{overallStats.outbound_calls || 0}</div>
              <div className="text-sm text-gray-600 font-medium">Outbound</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {overallStats.answer_rate != null ? Number(overallStats.answer_rate).toFixed(1) : '0.0'}%
              </div>
              <div className="text-sm text-gray-600 font-medium">{t('answerRate')}</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-3xl font-bold text-indigo-600 mb-2">
                   {Math.floor(Number(overallStats.avg_talk_time) || 0)}s
                 </div>
                 <div className="text-sm text-gray-600 font-medium">{t('avgDuration')}</div>
                </div>
              </div>
            )}
            {hourlyStats.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('callsByHour')}</h3>
                <div className="grid grid-cols-12 gap-2">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = hourlyStats.filter(s => s.stat_hour === hour);
                    const totalCalls = hourData.reduce((sum, s) => sum + parseInt(s.total_calls), 0);
                    const maxCalls = Math.max(...hourlyStats.map(s => parseInt(s.total_calls)));
                    const height = maxCalls > 0 ? (totalCalls / maxCalls) * 100 : 0;

                    return (
                      <div key={hour} className="flex flex-col items-center">
                        <div className="w-full bg-gray-100 rounded-t" style={{ height: '120px', display: 'flex', alignItems: 'flex-end' }}>
                          <div
                            className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                            style={{ height: `${height}%` }}
                            title={`${hour}:00 - ${totalCalls} calls`}
                          />
                        </div>
                        <div className="text-xs text-gray-600 mt-1">{hour}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Statistics View */}
        {currentView === 'agent-stats' && (
          <div className={`${theme.card} rounded-xl shadow-sm border ${theme.cardBorder} p-6`}>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold ${theme.text}`}>{t('myStatistics')}</h2>
              <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Ваша ефективність та активність дзвінків' : 'Your call performance and activity'}</p>
            </div>

            {agentStats && agentStats.length > 0 ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-3xl font-bold text-blue-600">
                      {agentStats.reduce((sum, stat) => sum + parseInt(stat.total_calls || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Total Calls</div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-3xl font-bold text-green-600">
                      {agentStats.reduce((sum, stat) => sum + parseInt(stat.answered_calls || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Answered</div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-3xl font-bold text-purple-600">
                      {Math.floor(agentStats.reduce((sum, stat) => sum + parseInt(stat.total_talk_time || 0), 0) / 60)}m
                    </div>
                    <div className="text-sm text-gray-600 font-medium">{t('totalTalkTime')}</div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-3xl font-bold text-orange-600">
                      {(() => {
                        const validStats = agentStats.filter(s => s.avg_talk_time > 0);
                        const avg = validStats.length > 0
                          ? validStats.reduce((sum, s) => sum + parseFloat(s.avg_talk_time), 0) / validStats.length
                          : 0;
                        return Math.floor(avg);
                      })()}s
                    </div>
                    <div className="text-sm text-gray-600 font-medium">{t('avgCallTime')}</div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${theme.cardBorder}`}>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Date</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Queue</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>Direction</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>{t('totalCalls')}</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>{t('answered')}</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>{t('totalTalkTime')}</th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text}`}>{t('avgCallTime')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentStats.map((stat, idx) => (
                        <tr key={idx} className={`border-b ${theme.cardBorder} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                          <td className={`px-4 py-3 text-sm ${theme.text}`}>
                            {new Date(stat.stat_date).toLocaleDateString('uk-UA', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                          </td>
                          <td className={`px-4 py-3 text-sm ${theme.textSecondary}`}>
                            {stat.direction === 'OUTBOUND' ? 'Outbound' : stat.queue_name}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              stat.direction === 'INBOUND'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {stat.direction === 'INBOUND' ? '📞 In' : '📱 Out'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm ${theme.text} font-semibold`}>{stat.total_calls}</td>
                          <td className={`px-4 py-3 text-sm ${theme.textSecondary}`}>{stat.answered_calls}</td>
                          <td className={`px-4 py-3 text-sm ${theme.textSecondary} font-mono`}>
                            {Math.floor(stat.total_talk_time / 60)}m {stat.total_talk_time % 60}s
                          </td>
                          <td className={`px-4 py-3 text-sm ${theme.textSecondary} font-mono`}>
                            {stat.avg_talk_time ? Math.floor(stat.avg_talk_time) : 0}s
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No statistics available yet</p>
              </div>
            )}
          </div>
        )}

{/* Contacts View */}
{currentView === 'contacts' && (
  <ContactBook 
    darkMode={darkMode}
    onCallContact={(phoneNumber) => {
      setCallNumber(phoneNumber);
      setTimeout(() => {
        if (sipConnected && !inCall) {
          handleCall();
        }
      }, 100);
    }}
  />
)}

{/* Category Management View */}
{currentView === 'category-management' && (
  <CategoryManagement 
    apiService={apiService}
    user={user}
  />
)}

{/* KPI Report View */}
{currentView === 'kpi-report' && (
  <div className={`${theme.card} rounded-xl shadow-sm border ${theme.cardBorder} p-6`}>
    <div className="mb-6">
      <h2 className={`text-2xl font-bold ${theme.text}`}>{t('kpiReport')}</h2>
      <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Показники ефективності агентів' : 'Agent performance indicators'}</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Початкова дата</label>
        <input
          type="date"
          value={kpiFilters.startDate}
          onChange={(e) => setKpiFilters({...kpiFilters, startDate: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Кінцева дата</label>
        <input
          type="date"
          value={kpiFilters.endDate}
          onChange={(e) => setKpiFilters({...kpiFilters, endDate: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Черга</label>
        <select
          value={kpiFilters.queue}
          onChange={(e) => setKpiFilters({...kpiFilters, queue: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Всі черги</option>
          {Object.entries(queues).map(([qid, q]) => (
            <option key={qid} value={qid}>{q.name}</option>
          ))}
        </select>
      </div>
    </div>

    <div className="flex justify-between items-center mb-4">
      <button
        onClick={loadKPIReport}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
      >
        <BarChart3 className="w-4 h-4" />
        <span>Згенерувати звіт</span>
      </button>
      <button
        onClick={() => {
          const url = apiService.getKPIReportExportURL(kpiFilters.startDate, kpiFilters.endDate, kpiFilters.queue);
          window.open(url, '_blank');
        }}
        disabled={kpiReport.length === 0}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        <Download className="w-4 h-4" />
        <span>Експорт в Excel</span>
      </button>
    </div>

    {kpiReport.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            {/* First header row - Main groups */}
            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <th rowSpan="2" className="border border-blue-500 px-3 py-3 text-left font-semibold sticky left-0 bg-blue-600 z-10">
                ПІБ
              </th>
              <th rowSpan="2" className="border border-blue-500 px-3 py-3 text-left font-semibold">
                SIP співробітника
              </th>
              <th colSpan="9" className="border border-blue-500 px-3 py-2 text-center font-bold text-base">
                📊 Показники дзвінка
              </th>
              <th colSpan="3" className="border border-blue-500 px-3 py-2 text-center font-bold text-base">
                ⏱️ Показники логіна
              </th>
            </tr>

            {/* Second header row - Sub-columns */}
            <tr className="bg-blue-500 text-white">
              {/* Показники дзвінка sub-columns */}
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Кількість прийнятих дзвінків всього
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Прийнято до 20 сек
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[100px]">
                Сер. трив. (хв)
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Сер. трив. th-support-spp
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Сер. трив. comfy-sale
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[100px]">
                Дзвінків th-support-spp
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[100px]">
                Дзвінків comfy-sale
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[100px]">
                Service Level %
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[100px]">
                Дзвінків/годину
              </th>

              {/* Показники логіна sub-columns */}
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Час в чергах
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Час в дзвінках
              </th>
              <th className="border border-blue-400 px-2 py-2 text-xs font-medium min-w-[120px]">
                Час в hold
              </th>
            </tr>
          </thead>
          <tbody>
            {kpiReport.map((agent, idx) => (
              <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                <td className="border border-gray-300 px-3 py-2 font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                  {agent.full_name || agent.ldap_username}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-gray-700 font-mono">
                  {agent.sip_number}
                </td>

                {/* Показники дзвінка */}
                <td className="border border-gray-300 px-3 py-2 text-center font-semibold text-blue-600">
                  {agent.total_calls_answered}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    agent.calls_answered_under_20s > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {agent.calls_answered_under_20s}
                  </span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_talk_time_all ? Number(agent.avg_talk_time_all).toFixed(2) : '-'}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_talk_time_support_spp ? Number(agent.avg_talk_time_support_spp).toFixed(2) : '-'}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_talk_time_comfy_sale ? Number(agent.avg_talk_time_comfy_sale).toFixed(2) : '-'}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-purple-600 font-medium">
                  {agent.calls_support_spp}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-purple-600 font-medium">
                  {agent.calls_comfy_sale}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${
                    agent.service_level >= 80 ? 'bg-green-100 text-green-700' :
                    agent.service_level >= 60 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {Number(agent.service_level).toFixed(1)}%
                  </span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.calls_per_hour ? Number(agent.calls_per_hour).toFixed(2) : '0.00'}
                </td>

                {/* Показники логіна */}
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <div className="text-gray-900 font-medium">
                    {formatTimeHHMM(agent.total_time_in_queues)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {agent.total_time_in_queues} сек
                  </div>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <div className="text-gray-900 font-medium">
                    {formatTimeHHMM(agent.total_time_in_calls)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {agent.total_time_in_calls} сек
                  </div>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <div className="text-orange-600 font-medium">
                    {formatTimeHHMM(agent.total_time_on_hold)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {agent.total_time_on_hold} сек
                  </div>
                </td>
              </tr>
            ))}

            {/* Summary row */}
            <tr className="bg-gradient-to-r from-gray-100 to-gray-200 font-bold">
              <td colSpan="2" className="border border-gray-400 px-3 py-3 text-right">
                РАЗОМ:
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center text-blue-700">
                {kpiReport.reduce((sum, a) => sum + (a.total_calls_answered || 0), 0)}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center">
                {kpiReport.reduce((sum, a) => sum + (a.calls_answered_under_20s || 0), 0)}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center">
                {(kpiReport.reduce((sum, a) => sum + (Number(a.avg_talk_time_all) || 0), 0) / kpiReport.filter(a => a.avg_talk_time_all).length || 0).toFixed(2)}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center">-</td>
              <td className="border border-gray-400 px-3 py-3 text-center">-</td>
              <td className="border border-gray-400 px-3 py-3 text-center text-purple-700">
                {kpiReport.reduce((sum, a) => sum + (a.calls_support_spp || 0), 0)}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center text-purple-700">
                {kpiReport.reduce((sum, a) => sum + (a.calls_comfy_sale || 0), 0)}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center">-</td>
              <td className="border border-gray-400 px-3 py-3 text-center">-</td>
              <td className="border border-gray-400 px-3 py-3 text-center">
                {formatTimeHHMM(kpiReport.reduce((sum, a) => sum + (a.total_time_in_queues || 0), 0))}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center">
                {formatTimeHHMM(kpiReport.reduce((sum, a) => sum + (a.total_time_in_calls || 0), 0))}
              </td>
              <td className="border border-gray-400 px-3 py-3 text-center text-orange-700">
                {formatTimeHHMM(kpiReport.reduce((sum, a) => sum + (a.total_time_on_hold || 0), 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    ) : (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p>Натисніть "Згенерувати звіт" для перегляду KPI</p>
      </div>
    )}
  </div>
)}

{/* KPI 1st Line Report View */}
{currentView === 'kpi-1st-line' && (
  <div className={`${theme.card} rounded-xl shadow-sm border ${theme.cardBorder} p-6`}>
    <div className="mb-6">
      <h2 className={`text-2xl font-bold ${theme.text}`}>
        {language === 'uk' ? 'KPI Звіт - 1 Лінія' : 'KPI Report - 1st Line'}
      </h2>
      <p className={`${theme.textSecondary} mt-1`}>
        {language === 'uk' ? 'Показники ефективності агентів підтримки' : 'Support agent performance indicators'}
      </p>
    </div>

    {/* Filters */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {language === 'uk' ? 'Початкова дата' : 'Start Date'}
        </label>
        <input
          type="date"
          value={kpiFilters.startDate}
          onChange={(e) => setKpiFilters({...kpiFilters, startDate: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {language === 'uk' ? 'Кінцева дата' : 'End Date'}
        </label>
        <input
          type="date"
          value={kpiFilters.endDate}
          onChange={(e) => setKpiFilters({...kpiFilters, endDate: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {language === 'uk' ? 'Черга' : 'Queue'}
        </label>
        <select
          value={kpiFilters.queue}
          onChange={(e) => setKpiFilters({...kpiFilters, queue: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{language === 'uk' ? 'Всі черги' : 'All Queues'}</option>
          {Object.entries(queues).map(([qid, q]) => (
            <option key={qid} value={qid}>{q.name}</option>
          ))}
        </select>
      </div>
    </div>

    {/* Action Buttons */}
    <div className="flex justify-between items-center mb-4">
      <button
        onClick={loadKPIReport}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
      >
        <BarChart3 className="w-4 h-4" />
        <span>{language === 'uk' ? 'Згенерувати звіт' : 'Generate Report'}</span>
      </button>
      <button
        onClick={() => {
          const url = apiService.getKPIReportExportURL(kpiFilters.startDate, kpiFilters.endDate, kpiFilters.queue);
          window.open(url, '_blank');
        }}
        disabled={kpiReport.length === 0}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        <Download className="w-4 h-4" />
        <span>{language === 'uk' ? 'Експорт в Excel' : 'Export to Excel'}</span>
      </button>
    </div>

    {/* KPI Table */}
    {kpiReport.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            {/* Main Header Row */}
            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <th rowSpan="2" className="border border-blue-500 px-3 py-3 text-left font-semibold sticky left-0 bg-blue-600 z-10 min-w-[150px]">
                ПІБ
              </th>
              <th rowSpan="2" className="border border-blue-500 px-3 py-3 text-center font-semibold min-w-[80px]">
                SIP
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Прийнято<br/>всього
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[70px]">
                %<br/>прийнятих
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[70px]">
                Дзвінків<br/>&gt;8хв
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Прийнято<br/>&lt;20с
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Сер. трив.<br/>(хв)
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Сер. трив.<br/>1л
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Service<br/>Level %
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[70px]">
                Дзвінків/<br/>год
              </th>
              <th colSpan="2" className="border border-blue-500 px-2 py-2 text-center font-semibold text-xs">
                Час в чергах
              </th>
              <th colSpan="2" className="border border-blue-500 px-2 py-2 text-center font-semibold text-xs">
                Час в дзвінках
              </th>
              <th colSpan="2" className="border border-blue-500 px-2 py-2 text-center font-semibold text-xs">
                Час в hold
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[80px]">
                Сер.<br/>очікування<br/>(с)
              </th>
              <th rowSpan="2" className="border border-blue-500 px-2 py-2 text-center text-xs min-w-[70px]">
                Вихідні<br/>дзвінки
              </th>
            </tr>

            {/* Sub-Header Row */}
            <tr className="bg-blue-500 text-white">
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">сек</th>
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">год:хв</th>
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">сек</th>
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">год:хв</th>
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">сек</th>
              <th className="border border-blue-400 px-2 py-1 text-xs min-w-[60px]">хв</th>
            </tr>
          </thead>

          <tbody>
            {kpiReport.map((agent, idx) => (
              <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                {/* Name */}
                <td className="border border-gray-300 px-3 py-2 font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                  {agent.full_name || agent.ldap_username}
                </td>

                {/* SIP */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700 font-mono text-xs">
                  {agent.sip_number}
                </td>

                {/* Total Answered */}
                <td className="border border-gray-300 px-3 py-2 text-center font-bold text-blue-600">
                  {agent.total_calls_answered || 0}
                </td>

                {/* Answer Rate % */}
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                    (agent.answer_rate_percent || 0) >= 80 ? 'bg-green-100 text-green-800' :
                    (agent.answer_rate_percent || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {agent.answer_rate_percent || 0}%
                  </span>
                </td>

                {/* Calls >8min */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.calls_over_8_minutes || 0}
                </td>

                {/* Answered <20s */}
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                    (agent.calls_answered_under_20s || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {agent.calls_answered_under_20s || 0}
                  </span>
                </td>

                {/* Avg Duration (all) */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_talk_time_all || '0.00'}
                </td>

                {/* Avg Duration (1st line) */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_talk_time_1st_line || '0.00'}
                </td>

                {/* Service Level % */}
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-bold ${
                    (agent.service_level_percent || 0) >= 80 ? 'bg-green-100 text-green-700' :
                    (agent.service_level_percent || 0) >= 60 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {agent.service_level_percent || 0}%
                  </span>
                </td>

                {/* Calls per Hour */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.calls_per_hour || '0'}
                </td>

                {/* Time in Queues - Seconds */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-600 text-xs">
                  {agent.time_in_queues_sec || 0}
                </td>

                {/* Time in Queues - Formatted */}
                <td className="border border-gray-300 px-3 py-2 text-center font-mono text-gray-900">
                  {agent.time_in_queues_formatted || '00:00'}
                </td>

                {/* Time in Calls - Seconds */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-600 text-xs">
                  {agent.time_in_calls_sec || 0}
                </td>

                {/* Time in Calls - Formatted */}
                <td className="border border-gray-300 px-3 py-2 text-center font-mono text-gray-900">
                  {agent.time_in_calls_formatted || '00:00'}
                </td>

                {/* Time in Hold - Seconds */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-600 text-xs">
                  {agent.time_in_hold_sec || 0}
                </td>

                {/* Time in Hold - Minutes */}
                <td className="border border-gray-300 px-3 py-2 text-center text-orange-600 font-medium">
                  {agent.time_in_hold_min || '0.00'}
                </td>

                {/* Avg Wait Time */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.avg_wait_time_sec || '0.00'}
                </td>

                {/* Outbound Calls */}
                <td className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                  {agent.outbound_calls || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>{language === 'uk' ? 'Натисніть "Згенерувати звіт" для перегляду даних' : 'Click "Generate Report" to view data'}</p>
      </div>
    )}
  </div>
)}

{/* Tags Management View */}
{currentView === 'tags' && (
  <div className={`${theme.card} rounded-xl shadow-sm border ${theme.cardBorder} p-6`}>
    <div className="mb-6">
      <h2 className={`text-2xl font-bold ${theme.text}`}>{t('tagsManagement')}</h2>
      <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Створення та управління тегами дзвінків' : 'Create and manage call tags'}</p>
    </div>

    {/* Add New Tag Form */}
    <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 mb-6`}>
      <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>{language === 'uk' ? 'Додати новий тег' : 'Add New Tag'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>{language === 'uk' ? 'Назва тегу' : 'Tag Name'}</label>
          <input
            type="text"
            value={newTag.tag_name}
            onChange={(e) => setNewTag({...newTag, tag_name: e.target.value})}
            placeholder={language === 'uk' ? 'напр., Важливо' : 'e.g., Important'}
            className={`w-full px-3 py-2 border ${theme.cardBorder} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.input}`}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>{language === 'uk' ? 'Колір' : 'Color'}</label>
          <div className="flex space-x-2">
            <input
              type="color"
              value={newTag.tag_color}
              onChange={(e) => setNewTag({...newTag, tag_color: e.target.value})}
              className={`w-16 h-10 border ${theme.cardBorder} rounded cursor-pointer`}
            />
            <input
              type="text"
              value={newTag.tag_color}
              onChange={(e) => setNewTag({...newTag, tag_color: e.target.value})}
              className={`flex-1 px-3 py-2 border ${theme.cardBorder} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.input}`}
            />
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>{language === 'uk' ? 'Категорія' : 'Category'}</label>
          <input
            type="text"
            value={newTag.tag_category}
            onChange={(e) => setNewTag({...newTag, tag_category: e.target.value})}
            placeholder={language === 'uk' ? 'напр., Пріоритет' : 'e.g., Priority'}
            className={`w-full px-3 py-2 border ${theme.cardBorder} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.input}`}
          />
        </div>

        <div className="flex items-end">
          <button
            onClick={async () => {
              try {
                await apiService.createTag(newTag);
                setNewTag({ tag_name: '', tag_color: '#3B82F6', tag_category: '' });
                loadTags();
                alert('✅ Tag created successfully');
              } catch (error) {
                alert('❌ Failed to create tag: ' + error.message);
              }
            }}
            disabled={!newTag.tag_name}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Add Tag
          </button>
        </div>
      </div>
    </div>

    {/* Tags List */}
    <div>
      <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>{language === 'uk' ? 'Існуючі теги' : 'Existing Tags'}</h3>
      {availableTags.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableTags.map((tag) => (
            <div key={tag.id} className={`border ${theme.cardBorder} rounded-lg p-4 hover:shadow-md transition-shadow ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full border-2"
                    style={{ backgroundColor: tag.tag_color || '#3B82F6' }}
                  ></div>
                  <span className="font-semibold text-gray-900">{tag.tag_name}</span>
                </div>
                <button
                  onClick={async () => {
                    if (window.confirm(`Delete tag "${tag.tag_name}"?`)) {
                      try {
                        await apiService.request(`/tags/${tag.id}`, { method: 'DELETE' });
                        loadTags();
                        alert('✅ Tag deleted');
                      } catch (error) {
                        alert('❌ Failed to delete tag');
                      }
                    }
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {tag.tag_category && (
                <div className="text-sm text-gray-600">
                  Category: <span className="font-medium">{tag.tag_category}</span>
                </div>
              )}
              <div className="text-xs text-gray-400 mt-2">
                Created: {new Date(tag.created_at).toLocaleDateString('uk-UA')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Tag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>No tags created yet</p>
        </div>
      )}
    </div>
  </div>
)}

{/* All Queues View */}
{currentView === 'all-queues' && (
  <div>
    <div className="mb-6">
      <h2 className={`text-2xl font-bold ${theme.text}`}>{t('allQueues')}</h2>
      <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Повний огляд усіх черг дзвінків у системі' : 'Complete view of all call queues in the system'}</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="text-3xl font-bold text-blue-600">
          {Object.keys(queues).length}
        </div>
        <div className="text-sm text-gray-600 font-medium">Total Queues</div>
      </div>

      <div className="bg-orange-50 rounded-lg p-4">
        <div className="text-3xl font-bold text-orange-600">
          {Object.values(queues).reduce((sum, q) => sum + (q.waiting || 0), 0)}
        </div>
        <div className="text-sm text-gray-600 font-medium">Total Waiting</div>
      </div>

      <div className="bg-purple-50 rounded-lg p-4">
        <div className="text-3xl font-bold text-purple-600">
          {Object.values(queues).reduce((sum, q) => sum + (q.agents || 0), 0)}
        </div>
        <div className="text-sm text-gray-600 font-medium">Total Agents</div>
      </div>

      <div className="bg-green-50 rounded-lg p-4">
        <div className="text-3xl font-bold text-green-600">
          {Object.values(queues).reduce((sum, q) => sum + (q.available || 0), 0)}
        </div>
        <div className="text-sm text-gray-600 font-medium">Available Agents</div>
      </div>
    </div>


    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Queue Name</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Waiting</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Agents</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Available</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Members</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(queues).map(([queueId, queue]) => (
            <tr key={queueId} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="font-semibold text-gray-900">{queue.name}</div>
                <div className="text-xs text-gray-500 font-mono">{queueId}</div>
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold ${
                  queue.waiting > 5 ? 'bg-red-100 text-red-600' :
                  queue.waiting > 2 ? 'bg-orange-100 text-orange-600' :
                  queue.waiting > 0 ? 'bg-yellow-100 text-yellow-600' :
                  'bg-green-100 text-green-600'
                }`}>
                  {queue.waiting}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span className="text-lg font-semibold text-gray-900">{queue.agents}</span>
              </td>
              <td className="px-4 py-3 text-center">
                <span className="text-lg font-semibold text-green-600">{queue.available}</span>
              </td>
              <td className="px-4 py-3 text-center">
                {queue.available > 0 ? (
                  <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    ✓ Ready
                  </span>
                ) : queue.agents > 0 ? (
                  <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                    ⏸ Busy
                  </span>
                ) : (
                  <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                    ✗ No Agents
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                {queue.members && queue.members.length > 0 ? (
                  <div className="space-y-1">
                    {queue.members.slice(0, 3).map((member, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-xs">
                        <div className={`w-2 h-2 rounded-full ${
                          member.statusColor === 'green' ? 'bg-green-500' :
                          member.statusColor === 'red' ? 'bg-red-500' :
                          member.statusColor === 'yellow' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}></div>
                        <span className="text-gray-700">{member.displayName || `SIP/${member.sipNumber}`}</span>
                      </div>
                    ))}
                    {queue.members.length > 3 && (
                      <div className="text-xs text-gray-500">+{queue.members.length - 3} more</div>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No agents</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

{currentView === 'user-management' && (
  <div>
    <div className="mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text}`}>{t('userManagement')}</h2>
          <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Керування користувачами, дозволами та доступом до черг' : 'Manage users, permissions, and queue access'}</p>
        </div>
        <button
          onClick={() => {
            resetUserForm();
            loadAvailableQueuesForAssignment();
            loadAvailableSipNumbers();
            setShowUserModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Username</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Full Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">SIP</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Permissions</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Queues</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.ldap_username} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-900 font-medium">{user.ldap_username}</td>
              <td className="px-4 py-3 text-sm text-gray-700">{user.full_name || '-'}</td>
              <td className="px-4 py-3 text-sm text-gray-700 font-mono">{user.sip_number}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{user.email || '-'}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {user.is_admin && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      Admin
                    </span>
                  )}
                  {user.can_view_kpi && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      KPI
                    </span>
                  )}
                  {user.can_manage_queues && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Queues
                    </span>
                  )}
                  {user.can_edit_tags && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                      Tags
                    </span>
                  )}
                  {user.can_manage_users && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Users
                    </span>
                  )}
                  {!user.is_admin && !user.can_view_kpi && !user.can_manage_queues && !user.can_edit_tags && !user.can_manage_users && (
                    <span className="text-xs text-gray-400">No special permissions</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {user.allowed_queues?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {user.allowed_queues.slice(0, 2).map((queue, idx) => (
                      <span key={idx} className="inline-flex px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                        {queue}
                      </span>
                    ))}
                    {user.allowed_queues.length > 2 && (
                      <span className="inline-flex px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                        +{user.allowed_queues.length - 2}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">No queues</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex space-x-2">
                  <button
                    onClick={() => openEditUserModal(user)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit user"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.ldap_username)}
                    className="text-red-600 hover:text-red-800"
                    title="Deactivate user"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {users.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>No users found</p>
        </div>
      )}
    </div>
  </div>
)}

      </div>  {/* ← CRITICAL: Close Main Content Area (max-w-full px-6 py-6 pb-32) */}

{/* User Creation/Edit Modal */}
{showUserModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {editingUser ? 'Edit User' : 'Create New User'}
        </h2>
        <button
          onClick={() => {
            setShowUserModal(false);
            resetUserForm();
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LDAP Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userForm.ldap_username}
                onChange={(e) => setUserForm({...userForm, ldap_username: e.target.value})}
                disabled={!!editingUser}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SIP Number <span className="text-red-500">*</span>
              </label>
              <select
                value={userForm.sip_number}
                onChange={(e) => setUserForm({...userForm, sip_number: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {editingUser && (
                  <option value={editingUser.sip_number}>{editingUser.sip_number} (current)</option>
                )}
                {!editingUser && <option value="">Select SIP number...</option>}
                {availableSipNumbers.map(sip => (
                  <option key={sip.sip_number} value={sip.sip_number}>
                    {sip.sip_number} {sip.full_name ? `- ${sip.full_name}` : ''}
                  </option>
                ))}
              </select>
              {editingUser && userForm.sip_number !== editingUser.sip_number && (
                <p className="text-sm text-orange-600 mt-1 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>Warning: Changing SIP number will disconnect active sessions and require re-login</span>
                </p>
              )}
            </div>

            <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> {editingUser ? 'You can change the SIP number. All queue memberships and notifications will be migrated automatically.' : 'Select a pre-configured SIP number. The password is already set in the system.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={userForm.full_name}
                onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="john@example.com"
              />
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
              <input
                type="checkbox"
                checked={userForm.permissions.is_admin}
                onChange={(e) => setUserForm({
                  ...userForm,
                  permissions: {...userForm.permissions, is_admin: e.target.checked}
                })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Administrator</span>
                <p className="text-sm text-gray-600">Full access to all features and settings</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
              <input
                type="checkbox"
                checked={userForm.permissions.can_view_kpi}
                onChange={(e) => setUserForm({
                  ...userForm,
                  permissions: {...userForm.permissions, can_view_kpi: e.target.checked}
                })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">View KPI Reports</span>
                <p className="text-sm text-gray-600">Access to agent performance reports and analytics</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
              <input
                type="checkbox"
                checked={userForm.permissions.can_manage_queues}
                onChange={(e) => setUserForm({
                  ...userForm,
                  permissions: {...userForm.permissions, can_manage_queues: e.target.checked}
                })}
                className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Manage Queues</span>
                <p className="text-sm text-gray-600">Add/remove agents from queues and manage queue settings</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
              <input
                type="checkbox"
                checked={userForm.permissions.can_edit_tags}
                onChange={(e) => setUserForm({
                  ...userForm,
                  permissions: {...userForm.permissions, can_edit_tags: e.target.checked}
                })}
                className="w-5 h-5 text-yellow-600 rounded focus:ring-2 focus:ring-yellow-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Edit Tags</span>
                <p className="text-sm text-gray-600">Create, modify, and delete call tags</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors">
              <input
                type="checkbox"
                checked={userForm.permissions.can_manage_users}
                onChange={(e) => setUserForm({
                  ...userForm,
                  permissions: {...userForm.permissions, can_manage_users: e.target.checked}
                })}
                className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Manage Users</span>
                <p className="text-sm text-gray-600">Create users and assign permissions</p>
              </div>
            </label>
          </div>
        </div>

        {/* Queue Assignment */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Queue Access (Login)</h3>
          <p className="text-sm text-gray-600 mb-3">Queues the user can login to and handle calls</p>
          <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
            {availableQueuesForAssignment.length > 0 ? (
              <div className="space-y-2">
                {availableQueuesForAssignment.map((queue) => (
                  <label key={queue.queue_name} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userForm.queues.includes(queue.queue_name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUserForm({
                            ...userForm,
                            queues: [...userForm.queues, queue.queue_name]
                          });
                        } else {
                          setUserForm({
                            ...userForm,
                            queues: userForm.queues.filter(q => q !== queue.queue_name)
                          });
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">{queue.display_name || queue.queue_name}</span>
                      <span className="text-sm text-gray-500 ml-2">({queue.queue_name})</span>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No queues available</p>
              </div>
            )}
          </div>
        </div>

        {/* Viewable Queues */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span>Viewable Queues (View Only)</span>
            <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">NEW</span>
          </h3>
          <p className="text-sm text-gray-600 mb-3">Queues the user can view statistics for (without login access)</p>
          <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4 max-h-64 overflow-y-auto">
            {availableQueuesForAssignment.length > 0 ? (
              <div className="space-y-2">
                {availableQueuesForAssignment.map((queue) => (
                  <label key={`view-${queue.queue_name}`} className="flex items-center space-x-3 p-2 hover:bg-yellow-100 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(userForm.viewableQueues || []).includes(queue.queue_name)}
                      onChange={(e) => {
                        const currentViewable = userForm.viewableQueues || [];
                        if (e.target.checked) {
                          setUserForm({
                            ...userForm,
                            viewableQueues: [...currentViewable, queue.queue_name]
                          });
                        } else {
                          setUserForm({
                            ...userForm,
                            viewableQueues: currentViewable.filter(q => q !== queue.queue_name)
                          });
                        }
                      }}
                      className="w-4 h-4 text-yellow-600 rounded focus:ring-2 focus:ring-yellow-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">{queue.display_name || queue.queue_name}</span>
                      <span className="text-sm text-gray-500 ml-2">({queue.queue_name})</span>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No queues available</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              setShowUserModal(false);
              resetUserForm();
            }}
            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={editingUser ? handleUpdateUser : handleCreateUser}
            disabled={!userForm.ldap_username || !userForm.sip_number}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {editingUser ? 'Update User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Queue Management View */}
{currentView === 'queue-management' && (
  <QueueManagement theme={darkMode ? 'dark' : 'light'} language={language} />
)}

{/* Settings View */}
{currentView === 'settings' && (
  <div className={`${theme.card} rounded-xl shadow-sm border ${theme.cardBorder} p-6`}>
    <div className="mb-6">
      <h2 className={`text-2xl font-bold ${theme.text}`}>{t('settings')}</h2>
      <p className={`${theme.textSecondary} mt-1`}>{language === 'uk' ? 'Налаштуйте свій досвід' : 'Customize your experience'}</p>
    </div>

    {/* Ringtone Settings */}
    <div className="space-y-6">
      <div className={`border-b ${theme.cardBorder} pb-6`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4 flex items-center`}>
          <Volume2 className="w-5 h-5 mr-2" />
          {t('ringtoneSettings')}
        </h3>
        
        <div className="space-y-4">
          {/* Current Ringtone */}
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${theme.text}`}>{t('currentRingtone')}:</span>
              <span className={`text-sm ${theme.textSecondary}`}>
                {customRingtone && customRingtone.startsWith('data:') ? t('custom') : t('default')}
              </span>
            </div>
            
            {customRingtone && (
              <div className="flex items-center space-x-2 mt-3">
                <button
                  onClick={() => {
                    if (ringtoneRef.current) {
                      ringtoneRef.current.src = customRingtone;
                      ringtoneRef.current.volume = ringtoneVolume;
                      ringtoneRef.current.loop = false;
                      ringtoneRef.current.play();
                    }
                  }}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Play className="w-4 h-4 inline mr-1" />
                  {t('test')}
                </button>
                <button
                  onClick={handleRemoveRingtone}
                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  <X className="w-4 h-4 inline mr-1" />
                  {t('remove')}
                </button>
              </div>
            )}
          </div>

          {/* Upload Custom Ringtone */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              {t('uploadRingtone')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                accept="audio/*"
                onChange={handleRingtoneUpload}
                className={`block w-full text-sm ${theme.textSecondary} file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`}
              />
            </div>
            <p className={`text-xs ${theme.textSecondary} mt-1`}>
              Supported formats: MP3, WAV, OGG, M4A
            </p>
          </div>

          {/* Volume Control */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              {t('ringtoneVolume')}: {Math.round(ringtoneVolume * 100)}%
            </label>
            <div className="flex items-center space-x-3">
              <VolumeX className="w-5 h-5 text-gray-400" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={ringtoneVolume}
                onChange={(e) => handleRingtoneVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <Volume2 className="w-5 h-5 text-gray-700" />
            </div>
          </div>

          {/* Call End Sound Toggle */}
          <div>
            <label className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg cursor-pointer transition-colors`}>
              <input
                type="checkbox"
                checked={callEndSoundEnabled}
                onChange={(e) => {
                  setCallEndSoundEnabled(e.target.checked);
                  localStorage.setItem('callEndSoundEnabled', JSON.stringify(e.target.checked));
                }}
                className="w-5 h-5 text-blue-600 rounded"
              />
              <div className="flex-1">
                <span className={`font-medium ${theme.text}`}>{t('callEndSound')}</span>
                <p className={`text-sm ${theme.textSecondary}`}>
                  {language === 'uk' ? 'Відтворювати звук при завершенні дзвінка' : 'Play sound when call ends'}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div className={`border-b ${theme.cardBorder} pb-6`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>{t('language')}</h3>
        <div className="space-y-2">
          <label className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg cursor-pointer transition-colors`}>
            <input
              type="radio"
              name="language"
              checked={language === 'uk'}
              onChange={() => {
                setLanguage('uk');
                localStorage.setItem('language', 'uk');
              }}
              className="w-5 h-5 text-blue-600"
            />
            <div className="flex-1">
              <span className={`font-medium ${theme.text}`}>Українська</span>
              <p className={`text-sm ${theme.textSecondary}`}>Ukrainian language</p>
            </div>
          </label>
          <label className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg cursor-pointer transition-colors`}>
            <input
              type="radio"
              name="language"
              checked={language === 'en'}
              onChange={() => {
                setLanguage('en');
                localStorage.setItem('language', 'en');
              }}
              className="w-5 h-5 text-blue-600"
            />
            <div className="flex-1">
              <span className={`font-medium ${theme.text}`}>English</span>
              <p className={`text-sm ${theme.textSecondary}`}>English language</p>
            </div>
          </label>
        </div>
      </div>

      {/* Theme Settings */}
      <div className={`border-b ${theme.cardBorder} pb-6`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>{t('theme')}</h3>
        <label className={`flex items-center space-x-3 p-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg cursor-pointer transition-colors`}>
          <input
            type="checkbox"
            checked={darkMode}
            onChange={(e) => {
              setDarkMode(e.target.checked);
              localStorage.setItem('darkMode', JSON.stringify(e.target.checked));
            }}
            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex-1">
            <span className={`font-medium ${theme.text}`}>{t('darkMode')}</span>
            <p className={`text-sm ${theme.textSecondary}`}>Use dark theme for the interface</p>
          </div>
        </label>
      </div>

      {/* Info Section */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">About Custom Ringtones</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Upload your own ringtone for incoming calls</li>
          <li>• Ringtone is stored locally in your browser</li>
          <li>• Adjust volume to your preference</li>
          <li>• Test your ringtone before receiving calls</li>
        </ul>
      </div>
    </div>
  </div>
)}

{/* Call History Sidebar - Fixed positioning with proper spacing */}
{showCallHistorySidebar && (
  <>
    {/* Overlay to close sidebar when clicking outside */}
    <div
      className="fixed inset-0 bg-black bg-opacity-20 z-30"
      onClick={() => setShowCallHistorySidebar(false)}
    />

    <div className="fixed top-0 right-0 w-80 bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out border-l border-gray-200 overflow-hidden flex flex-col" style={{ bottom: '100px', height: 'calc(100vh - 100px)' }}>
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Clock className="w-5 h-5 text-white" />
          <h3 className="text-white font-semibold">{t('callHistory')}</h3>
        </div>
        <button
          onClick={() => setShowCallHistorySidebar(false)}
          className="text-white hover:text-gray-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4">
        {callHistory.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">{t('noCallHistory')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {callHistory.map((call) => (
              <button
                key={call.id}
                onClick={() => {
                  setCallNumber(call.number);
                  setShowCallHistorySidebar(false);
                }}
                className="w-full text-left p-3 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">
                      {call.type === 'incoming' ? '📞' : '📱'}
                    </span>
                    <div>
                      <div className="font-mono text-sm font-semibold text-gray-900">
                        {call.number}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(call.timestamp).toLocaleString('uk-UA', {
                          timeZone: 'Europe/Kiev',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    call.status === 'completed' ? 'bg-green-100 text-green-700' :
                    call.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {call.status}
                  </span>
                  {call.duration > 0 && (
                    <span className="text-xs text-gray-500">
                      {formatDuration(call.duration)}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Clear History Button - Fixed at bottom */}
      <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => {
            if (window.confirm(t('confirmClearHistory'))) {
              localStorage.removeItem(CALL_HISTORY_KEY);
              setCallHistory([]);
            }
          }}
          className="w-full px-4 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
        >
          🗑️ {t('clearHistory')}
        </button>
      </div>
    </div>
  </>
)}

      {/* Incoming Call Modal - Enhanced with Tagging */}
      {incomingCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 animate-pulse">
                <Phone className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Incoming Call</h2>
              {callerInfo && (
                <>
                  <p className="text-xl font-semibold text-gray-700">{callerInfo.name}</p>
                  <p className="text-lg text-gray-600 font-mono">{callerInfo.number}</p>
                </>
              )}
            </div>

            {/* Quick Tags */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Tags</label>
              <div className="flex flex-wrap gap-2">
                {availableTags.slice(0, 6).map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => {
                      if (incomingCallTags.includes(tag.tag_name)) {
                        setIncomingCallTags(incomingCallTags.filter(t => t !== tag.tag_name));
                      } else {
                        setIncomingCallTags([...incomingCallTags, tag.tag_name]);
                      }
                    }}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      incomingCallTags.includes(tag.tag_name)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag.tag_name}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Notes</label>
              <textarea
                value={incomingCallNotes}
                onChange={(e) => setIncomingCallNotes(e.target.value)}
                placeholder="Add notes about this call..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="3"
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => {
                  handleRejectCall();
                  setIncomingCallNotes('');
                  setIncomingCallTags([]);
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <PhoneOff className="w-5 h-5" />
                <span>Reject</span>
              </button>
              <button
                onClick={() => {
                  handleAcceptCall();
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>Accept</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Call Card - Stays open during call for notes and tags */}
      {activeCallCard && inCall && (
        <div className="fixed top-24 right-6 z-40 w-96 bg-white rounded-2xl shadow-2xl border-2 border-green-500">
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-300 rounded-full animate-pulse"></div>
                <h3 className="text-white font-semibold">
                  {activeCallCard.direction === 'incoming' ? '📞 Incoming Call' : '📱 Outgoing Call'}
                </h3>
              </div>
              <div className="text-white text-sm font-mono">
                {formatDuration(callDuration)}
              </div>
            </div>
            <div className="mt-2">
              <p className="text-white text-lg font-semibold">{activeCallCard.name}</p>
              <p className="text-green-100 text-sm font-mono">{activeCallCard.number}</p>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Call Status */}
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Status</span>
              <span className="text-sm font-semibold text-green-600">
                {onHold ? '⏸️ On Hold' : '🟢 Active'}
              </span>
            </div>

            {/* Tags Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                Call Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {activeCallCard.tags.map((tagName, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {tagName}
                    <button
                      onClick={() => {
                        setActiveCallCard({
                          ...activeCallCard,
                          tags: activeCallCard.tags.filter((_, i) => i !== idx)
                        });
                      }}
                      className="ml-2 hover:text-blue-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <select
                onChange={(e) => {
                  if (e.target.value && !activeCallCard.tags.includes(e.target.value)) {
                    setActiveCallCard({
                      ...activeCallCard,
                      tags: [...activeCallCard.tags, e.target.value]
                    });
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Add tag...</option>
                {availableTags
                  .filter(tag => !activeCallCard.tags.includes(tag.tag_name))
                  .map(tag => (
                    <option key={tag.id} value={tag.tag_name}>
                      {tag.tag_name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Notes Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="w-4 h-4 inline mr-1" />
                Call Notes
              </label>
              <textarea
                value={activeCallCard.notes}
                onChange={(e) => {
                  setActiveCallCard({
                    ...activeCallCard,
                    notes: e.target.value
                  });
                }}
                placeholder="Type notes during the call..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                rows="4"
              />
              <p className="text-xs text-gray-500 mt-1">
                Notes will be saved automatically when the call ends
              </p>
            </div>

            {/* Quick Actions */}
            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2">Quick Actions</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowTransfer(!showTransfer)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    showTransfer
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <PhoneForwarded className="w-4 h-4 inline mr-1" />
                  Transfer
                </button>
                <button
                  onClick={toggleHold}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    onHold
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {onHold ? <Play className="w-4 h-4 inline mr-1" /> : <Pause className="w-4 h-4 inline mr-1" />}
                  {onHold ? 'Resume' : 'Hold'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Static Phone Bar - Bottom of Screen - Compact Version */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-800 shadow-2xl">
        <div className="max-w-full mx-auto px-4 py-2">
          <div className="flex items-center justify-between space-x-4">
            {/* Left: Status Indicator */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  inCall 
                    ? 'bg-blue-400 animate-pulse' 
                    : sipConnected 
                      ? 'bg-green-400' 
                      : 'bg-red-400'
                }`}></div>
                <span className="text-xs text-gray-300 font-medium">
                  {inCall ? (callNumber ? `In call with ${callNumber}` : 'In Call') : sipConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              
              {/* Microphone Status Indicator */}
              <div 
                className="flex items-center space-x-1 cursor-pointer" 
                onClick={checkMicrophoneStatus}
                title={microphoneStatus.available ? 'Microphone OK' : microphoneStatus.error || 'Microphone not available - Click to retry'}
              >
                {microphoneStatus.checking ? (
                  <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : microphoneStatus.available ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth={2} />
                  </svg>
                )}
              </div>
            </div>

            {/* Center: Number Input / Call Info with Call Button */}
            <div className="flex-1 max-w-md flex items-center space-x-2">
              {inCall ? (
                <div className="flex items-center justify-center space-x-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${onHold ? 'bg-yellow-400' : 'bg-green-400'}`}></div>
                    <span className="text-white text-sm">
                      {callStatus === 'dialing' && t('dialing')}
                      {callStatus === 'ringing' && t('ringing')}
                      {callStatus === 'connected' && (onHold ? t('hold') : t('connected'))}
                    </span>
                  </div>
                  <span className="text-white font-mono text-sm">{callNumber}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400 text-xs">{t('callDuration')}:</span>
                    <span className="text-white text-sm font-mono font-bold">{formatDuration(callDuration)}</span>
                  </div>
                  {onHold && (
                    <div className="flex items-center space-x-2 bg-yellow-900 bg-opacity-50 px-2 py-1 rounded">
                      <span className="text-yellow-300 text-xs">{t('holdTime')}:</span>
                      <span className="text-yellow-100 text-sm font-mono font-bold">{formatDuration(currentHoldDuration)}</span>
                    </div>
                  )}
                  {(totalHoldDuration > 0 || holdCount > 0) && !onHold && (
                    <div className="flex items-center space-x-2 text-xs text-gray-400">
                      <span>{t('totalHold')}: {formatDuration(totalHoldDuration)}</span>
                      <span>({holdCount}x)</span>
                    </div>
                  )}
                  {muted && (
                    <div className="flex items-center space-x-1 bg-red-900 bg-opacity-50 px-2 py-1 rounded">
                      <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth={2} />
                      </svg>
                      <span className="text-red-300 text-xs font-medium">Muted</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <PhoneNumberInput
                    value={callNumber}
                    onChange={setCallNumber}
                    onContactSelect={(contact) => {
                      console.log('Selected contact:', contact);
                      // You can add additional logic here when a contact is selected
                    }}
                    placeholder="Enter phone number"
                    className="flex-1 text-sm font-mono"
                    darkMode={darkMode}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !inCall && sipConnected && callNumber) {
                        handleCall();
                      }
                    }}
                  />
                  <button
                    onClick={handleCall}
                    disabled={!sipConnected || !callNumber}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-full p-2.5 transition-colors flex-shrink-0"
                    title="Call"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Right: Action Buttons - Transfer, Numpad, Call History */}
            <div className="flex items-center space-x-2">
              {/* Advanced Connection Quality Indicator (only show when in call) */}
              {inCall && (
                <div className="relative group">
                  <div className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-gray-700 cursor-help">
                    {/* Quality Score Circle */}
                    <div className={`w-3 h-3 rounded-full flex items-center justify-center text-xs font-bold ${
                      connectionQuality.status === 'good' ? 'bg-green-400 text-green-900' :
                      connectionQuality.status === 'fair' ? 'bg-yellow-400 text-yellow-900' :
                      connectionQuality.status === 'poor' ? 'bg-orange-400 text-orange-900' :
                      'bg-red-400 text-red-900'
                    }`}>
                      {connectionQuality.score > 0 ? Math.floor(connectionQuality.score / 10) : '!'}
                    </div>
                    
                    {/* RTT Display */}
                    <span className="text-xs text-gray-300">
                      {connectionQuality.rtt > 0 ? `${connectionQuality.rtt}ms` : '--'}
                    </span>
                    
                    {/* Trend Indicator */}
                    <span className="text-xs">
                      {networkTrends.improving ? '📈' :
                       networkTrends.degrading ? '📉' :
                       networkTrends.unstable ? '📊' :
                       connectionQuality.status === 'disconnected' ? '❌' : '📶'}
                    </span>
                    
                    {/* Packet Loss Warning */}
                    {connectionQuality.packetLoss > 5 && (
                      <span className="text-xs text-red-400">⚠️</span>
                    )}
                  </div>
                  
                  {/* Detailed Tooltip */}
                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50">
                    <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-64">
                      <div className="font-semibold mb-2">Connection Quality</div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <span className={`font-medium ${
                            connectionQuality.status === 'good' ? 'text-green-400' :
                            connectionQuality.status === 'fair' ? 'text-yellow-400' :
                            connectionQuality.status === 'poor' ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            {connectionQuality.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Quality Score:</span>
                          <span className="font-medium">{connectionQuality.score}/100</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Latency (RTT):</span>
                          <span className="font-medium">{connectionQuality.rtt}ms</span>
                        </div>
                        {connectionQuality.jitter > 0 && (
                          <div className="flex justify-between">
                            <span>Jitter:</span>
                            <span className="font-medium">{connectionQuality.jitter}ms</span>
                          </div>
                        )}
                        {connectionQuality.packetLoss > 0 && (
                          <div className="flex justify-between">
                            <span>Packet Loss:</span>
                            <span className="font-medium text-red-400">{connectionQuality.packetLoss}%</span>
                          </div>
                        )}
                        {networkTrends.recommendation && (
                          <div className="mt-2 pt-2 border-t border-gray-700">
                            <div className="text-yellow-400 font-medium">💡 Tip:</div>
                            <div className="text-gray-300">{networkTrends.recommendation}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Call Controls (only show when in call) */}
              {inCall && (
                <>
                  <button
                    onClick={() => setShowTransfer(!showTransfer)}
                    className={`${showTransfer ? 'bg-blue-600' : 'bg-gray-600'} hover:opacity-90 text-white rounded-full p-2 transition-colors`}
                    title="Transfer"
                  >
                    <PhoneForwarded className="w-4 h-4" />
                  </button>

                  <button
                    onClick={toggleHold}
                    className={`${onHold ? 'bg-yellow-500' : 'bg-gray-600'} hover:opacity-90 text-white rounded-full p-2 transition-colors`}
                    title={onHold ? 'Resume' : 'Hold'}
                  >
                    {onHold ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  </button>

                  <button
                    onClick={handleEndCall}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2.5 transition-colors"
                    title="End Call"
                  >
                    <PhoneOff className="w-5 h-5" />
                  </button>

                  <button
                    onClick={toggleSpeaker}
                    className={`${speakerMuted ? 'bg-red-500' : 'bg-gray-600'} hover:opacity-90 text-white rounded-full p-2 transition-colors`}
                    title={speakerMuted ? 'Unmute Speaker' : 'Mute Speaker'}
                  >
                    {speakerMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>

                  {/* Separator */}
                  <div className="w-px h-6 bg-gray-600 mx-1"></div>
                </>
              )}

              {/* Mute Button (always visible when in call) */}
              {inCall && (
                <button
                  onClick={toggleMute}
                  className={`${muted ? 'bg-red-500' : 'bg-gray-700'} text-white hover:opacity-90 rounded-lg p-2 transition-colors`}
                  title={muted ? 'Unmute' : 'Mute'}
                >
                  {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}

              {/* Numpad Button */}
              <button
                onClick={() => setShowDialPad(!showDialPad)}
                className={`p-2 rounded-lg transition-colors ${
                  showDialPad
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                title="Toggle Dial Pad"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              </button>

              {/* Call History Button */}
              {callHistory.length > 0 && (
                <button
                  onClick={() => setShowCallHistorySidebar(!showCallHistorySidebar)}
                  className="bg-gray-700 text-gray-300 hover:bg-gray-600 relative p-2 rounded-lg transition-colors"
                >
                  <Clock className="w-4 h-4" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-semibold">
                    {callHistory.length > 9 ? '9' : callHistory.length}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Collapsible Dial Pad */}
          {showDialPad && !inCall && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <div className="grid grid-cols-12 gap-1 max-w-sm mx-auto">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                  <button
                    key={digit}
                    onClick={() => handleDialPad(digit)}
                    className="col-span-4 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded py-2 text-lg font-semibold text-white transition-colors"
                  >
                    {digit}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Transfer UI */}
          {showTransfer && inCall && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <div className="flex items-center space-x-2">
                <select
                  value={transferType}
                  onChange={(e) => {
                    setTransferType(e.target.value);
                    setTransferTarget('');
                  }}
                  className="px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="queue">To Queue</option>
                  <option value="extension">To Extension</option>
                </select>

                {transferType === 'queue' ? (
                  <select
                    value={transferTarget}
                    onChange={(e) => setTransferTarget(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select queue...</option>
                    {allQueues.map((queue) => (
                      <option key={queue.queue_name} value={queue.queue_name}>
                        {queue.display_name || queue.queue_name} (ext: {queue.extension_number})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={transferTarget}
                    onChange={(e) => setTransferTarget(e.target.value)}
                    placeholder="Extension"
                    className="flex-1 px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                  />
                )}

                <button
                  onClick={handleTransfer}
                  disabled={!transferTarget}
                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  Transfer
                </button>
                <button
                  onClick={() => {
                    setShowTransfer(false);
                    setTransferTarget('');
                  }}
                  className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audio elements */}
      <audio ref={audioRef} className="hidden" />
      <audio ref={remoteAudioRef} playsInline autoPlay className="hidden" />
      <audio ref={ringtoneRef} playsInline className="hidden" />
    </div>
  );
};

export default WebRTCSIPClient;